/* scribbly-funnel.js
 * Drop-in modal/full-screen embed for the Scribbly preview funnel.
 *
 * Usage in Liquid:
 *   {% render 'scribbly-funnel' %}      -- include once in theme.liquid
 *   <button data-scribbly-funnel data-occasion="birthday">Preview Your Book</button>
 *
 * Behavior:
 *   - Desktop (>= 900px viewport): centered modal with dim backdrop, the
 *     funnel's own desktop modal styling shows through inside the iframe.
 *   - Mobile (< 900px): full-screen takeover, no chrome.
 *   - Close: X in the funnel iframe (postMessage 'scribbly:close'),
 *     ESC key, or click on the backdrop (desktop only).
 *   - Checkout: funnel posts 'scribbly:checkout' when the customer hits
 *     the "Bookshop" CTA. We close the modal and (TODO) navigate to the
 *     matching product page based on data-occasion.
 *
 * The iframe URL is hardcoded to the production Vercel deployment. When we
 * spin up a staging deploy, override at runtime via:
 *   window.SCRIBBLY_FUNNEL_URL = 'https://your-preview.vercel.app/preview';
 * before this script loads.
 */
(function () {
  'use strict';

  // Production funnel URL. Override globally before this script runs to
  // point the embed at a Vercel preview deploy during QA.
  var FUNNEL_URL =
    (typeof window !== 'undefined' && window.SCRIBBLY_FUNNEL_URL) ||
    'https://book-creator-delta.vercel.app/preview';

  // Origin allow-list for postMessage from the funnel iframe. We refuse
  // messages from any other origin so a malicious embedded page can't
  // spoof a 'scribbly:checkout' redirect.
  var ALLOWED_ORIGINS = [
    'https://book-creator-delta.vercel.app',
    // Vercel preview deploys live on *.vercel.app — wildcards aren't
    // supported by postMessage so we match by suffix below.
  ];
  function isAllowedOrigin(origin) {
    if (ALLOWED_ORIGINS.indexOf(origin) !== -1) return true;
    if (/^https:\/\/[a-z0-9-]+\.vercel\.app$/.test(origin)) return true;
    return false;
  }

  // Singleton state — we never need more than one modal open at once.
  var rootEl = null;
  var iframeEl = null;
  var lastOpener = null; // <-- restores focus when modal closes (a11y)
  // Open-transition bookkeeping: the load handler that reveals the iframe
  // (we re-bind per open and clear on close), and the safety-timeout id
  // that reveals the iframe even if `load` is delayed on slow networks.
  var iframeLoadHandler = null;
  var iframeLoadFallbackId = null;
  // Deferred src=about:blank teardown — we wait for the fade-out so the
  // about:blank flash isn't visible during close. Tracked so a fast
  // re-open can cancel the pending blank.
  var pendingBlankId = null;
  // Match the .scribbly-funnel-root opacity transition in the CSS. Update
  // both together if you change the duration.
  var FADE_MS = 200;
  // Safety net: if the iframe `load` event takes too long (slow phone
  // network, big initial bundle), reveal the iframe anyway. Better to
  // show a partially-loaded funnel than to leave the customer staring
  // at an empty dim backdrop.
  var IFRAME_LOAD_FALLBACK_MS = 1500;

  /** Build the modal DOM the first time we open. We keep the iframe
   *  detached from the page until the user actually clicks a CTA so we
   *  don't fire a request to /preview on every storefront page load. */
  function ensureRoot() {
    if (rootEl) return rootEl;
    rootEl = document.createElement('div');
    rootEl.className = 'scribbly-funnel-root';
    rootEl.setAttribute('aria-hidden', 'true');
    rootEl.innerHTML =
      '<div class="scribbly-funnel-backdrop" data-scribbly-backdrop></div>' +
      '<div class="scribbly-funnel-stage" role="dialog" aria-modal="true" aria-label="Preview your book"></div>';
    document.body.appendChild(rootEl);

    // Backdrop click closes (desktop). The CSS hides the backdrop on
    // mobile (full-screen takeover) so this listener is a no-op there.
    rootEl.querySelector('[data-scribbly-backdrop]').addEventListener('click', close);
    return rootEl;
  }

  /** Open the modal pointing at /preview with the given query params.
   *  Idempotent — clicking the same CTA twice while already open is a
   *  no-op rather than reloading the iframe. */
  function open(params) {
    ensureRoot();
    var stage = rootEl.querySelector('.scribbly-funnel-stage');

    if (!iframeEl) {
      iframeEl = document.createElement('iframe');
      iframeEl.className = 'scribbly-funnel-iframe';
      iframeEl.setAttribute('title', 'Scribbly book preview');
      // allow=clipboard-write lets the funnel copy the resume URL if we
      // ever surface that. allow=camera is for a possible future "take a
      // photo" path on mobile (currently the funnel uses file upload only).
      iframeEl.setAttribute('allow', 'clipboard-write; camera');
      stage.appendChild(iframeEl);
    }

    // Cancel any pending blank from a recent close — fast re-open shouldn't
    // race a delayed src=about:blank that wipes the URL we're about to set.
    if (pendingBlankId !== null) {
      clearTimeout(pendingBlankId);
      pendingBlankId = null;
    }

    // Reset the iframe's loaded state so the cream modal cross-fades over
    // the dim backdrop on every open, not just the first.
    iframeEl.classList.remove('is-loaded');
    if (iframeLoadHandler) {
      iframeEl.removeEventListener('load', iframeLoadHandler);
      iframeLoadHandler = null;
    }
    if (iframeLoadFallbackId !== null) {
      clearTimeout(iframeLoadFallbackId);
      iframeLoadFallbackId = null;
    }

    // Build the URL fresh each open. Four modes:
    //   - Default: open the funnel at /preview (occasion may be set)
    //   - Bookshop resume: skip /preview and deep-link to /shop/<slug>
    //   - Generation resume: open /preview?lead=<id> so the funnel hydrates
    //     from the existing lead row and lands the customer on whichever
    //     screen matches their progress (Waiting if still running, Reveal
    //     if illustration_url is populated)
    //   - Per-book CTA: open /preview?book=<handle>&kids=<slug,slug,…>.
    //     The funnel reads `book` to pin the originally-clicked book to
    //     the top of the bookshop after generation, and reads `kids` to
    //     show the ChooseKid screen (skip the photo/name/eye/age pipeline
    //     when the customer already has saved kids from prior previews).
    var url;
    if (params && params.bookshopSlug) {
      // FUNNEL_URL is the /preview path on the Vercel origin; swap the path
      // for /shop/<slug> while keeping the origin intact.
      var funnelOrigin = new URL(FUNNEL_URL).origin;
      url = new URL(funnelOrigin + '/shop/' + encodeURIComponent(params.bookshopSlug));
      // Restore express + book_id so the bookshop auto-opens BookDetail
      // for the originally-pinned book instead of rendering the catalog.
      if (params.express) url.searchParams.set('express', '1');
      if (params.bookId) url.searchParams.set('book_id', params.bookId);
      // Append the leadId on the URL fragment when we have it. The
      // bookshop iframe uses this to look up its portrait token in
      // localStorage (same-origin with the funnel) and authorize
      // /api/bookshop-* calls. Cross-device deep links won't have a
      // matching localStorage entry — the bookshop falls into its
      // degraded "locked" view in that case, which is the right
      // behavior. URL fragments are never sent to the server.
      if (params.leadId && UUID_RE.test(params.leadId)) {
        url.hash = 'leadId=' + encodeURIComponent(params.leadId);
      }
    } else if (params && params.resumeLeadId) {
      url = new URL(FUNNEL_URL);
      url.searchParams.set('lead', params.resumeLeadId);
      // Restore per-occasion entry context recorded with the original
      // resume save. Without these, a resume into the Juneteenth express
      // flow drops into the generic /preview path and skips the
      // reveal/iterations/bookshop-catalog-skipping logic that the funnel
      // and bookshop key off ?occasion / ?express / ?book_id.
      if (params.occasion) url.searchParams.set('occasion', params.occasion);
      if (params.express) url.searchParams.set('express', '1');
      if (params.bookId) url.searchParams.set('book_id', params.bookId);
    } else {
      url = new URL(FUNNEL_URL);
      if (params && params.occasion) url.searchParams.set('occasion', params.occasion);
      if (params && params.bookHandle) {
        url.searchParams.set('book', params.bookHandle);
        // Send the saved-kids list so the funnel can render ChooseKid
        // when the customer already has finished previews. Empty list
        // means "no saved kids yet" — funnel falls through to the
        // standard photo upload flow with `book` carried in state.
        //
        // We send TWO parallel comma-separated lists:
        //   - kids:    slug per saved kid (always present per entry)
        //   - leadIds: lead UUID per saved kid (empty token "" for legacy
        //              entries written before the leadId field existed)
        // The two arrays line up by index so ChooseKid can pair each
        // slug with its leadId (or null) and look up the matching
        // portrait_token on the funnel origin. Without the leadId,
        // /api/bookshop-session returns the gated locked shape and the
        // kid gets filtered from the picker.
        var savedKids = readSavedKids();
        if (savedKids.length > 0) {
          url.searchParams.set(
            'kids',
            savedKids.map(function (k) { return k.slug; }).join(','),
          );
          url.searchParams.set(
            'leadIds',
            savedKids
              .map(function (k) {
                // Legacy entries (pre-leadId-field) → empty placeholder;
                // ChooseKid treats those as unverifiable and drops them.
                return k.leadId && UUID_RE.test(k.leadId) ? k.leadId : '';
              })
              .join(','),
          );
        }
      }
    }
    url.searchParams.set('embed', '1');

    // Reveal the iframe once its document has loaded — or after the
    // safety fallback if `load` is delayed. The handler is idempotent
    // (`shown` guard) so the fallback firing after a real `load` is a
    // no-op. requestAnimationFrame defers the class flip to the next
    // paint so the opacity transition runs cleanly.
    var shown = false;
    iframeLoadHandler = function () {
      if (shown) return;
      shown = true;
      requestAnimationFrame(function () {
        if (iframeEl) iframeEl.classList.add('is-loaded');
      });
    };
    iframeEl.addEventListener('load', iframeLoadHandler);
    iframeLoadFallbackId = setTimeout(iframeLoadHandler, IFRAME_LOAD_FALLBACK_MS);

    iframeEl.src = url.toString();

    // Defer the .is-open flip to the next animation frame so the browser
    // has registered the root's opacity:0 baseline before the transition
    // to opacity:1 — required for the fade to actually animate on first
    // open (when the element was just appended to the DOM).
    requestAnimationFrame(function () {
      rootEl.classList.add('is-open');
      rootEl.setAttribute('aria-hidden', 'false');
    });
    document.documentElement.classList.add('scribbly-funnel-locked');
    document.addEventListener('keydown', onKeydown);
  }

  function close() {
    if (!rootEl) return;
    rootEl.classList.remove('is-open');
    // The bookshop's dedication-upsell handshake adds .is-overlay-active
    // when its scrim opens. Strip it here too in case the customer hit
    // the funnel × while the upsell was still open — otherwise the
    // class would survive into the next open() and stretch the stage
    // before the bookshop has a chance to ask for it.
    rootEl.classList.remove('is-overlay-active');
    rootEl.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('scribbly-funnel-locked');
    document.removeEventListener('keydown', onKeydown);

    // Tear down the open-transition listeners — `load` is no longer
    // interesting once we're closing, and the safety fallback would
    // otherwise reveal a hidden iframe mid-fade-out.
    if (iframeEl) iframeEl.classList.remove('is-loaded');
    if (iframeEl && iframeLoadHandler) {
      iframeEl.removeEventListener('load', iframeLoadHandler);
      iframeLoadHandler = null;
    }
    if (iframeLoadFallbackId !== null) {
      clearTimeout(iframeLoadFallbackId);
      iframeLoadFallbackId = null;
    }

    // Blank the iframe AFTER the fade-out finishes so the customer doesn't
    // see an about:blank flash through the closing modal. Also stops the
    // iframe from running timers / network during the fade. If a re-open
    // happens before the timer fires, open() cancels this id.
    if (iframeEl) {
      var ifrm = iframeEl;
      pendingBlankId = setTimeout(function () {
        pendingBlankId = null;
        if (rootEl && !rootEl.classList.contains('is-open')) {
          ifrm.src = 'about:blank';
        }
      }, FADE_MS + 20);
    }

    if (lastOpener && typeof lastOpener.focus === 'function') {
      try { lastOpener.focus(); } catch (e) { /* opener gone */ }
    }
    lastOpener = null;
  }

  function onKeydown(e) {
    if (e.key === 'Escape') close();
  }

  /** URLs that historically pointed customers at the legacy
   *  preview/order flow. Any anchor or button targeting one of these is
   *  treated as a funnel trigger automatically — no theme editing
   *  required. Existing CTAs in Shopify-Magic-generated blocks render
   *  with these hrefs, so this list is what wires them up. Add more
   *  patterns here when new legacy destinations show up; explicit
   *  data-scribbly-funnel attributes still take priority. */
  var FUNNEL_LINK_PATTERNS = [
    'wlcmapps.com/order-steps',
  ];

  /** True when `value` contains any legacy-funnel substring. Shared by the
   *  click handler and the href rewriter so both agree on what counts as a
   *  legacy CTA. */
  function matchesFunnelPattern(value) {
    if (!value) return false;
    for (var i = 0; i < FUNNEL_LINK_PATTERNS.length; i++) {
      if (value.indexOf(FUNNEL_LINK_PATTERNS[i]) !== -1) return true;
    }
    return false;
  }

  /* --- Legacy href rewriter ---
   *
   * The delegated click handler below already stops a plain left-click on a
   * legacy CTA from navigating. It cannot stop anything that bypasses that
   * handler:
   *   - cmd/ctrl-click and middle-click (browser opens the href directly)
   *   - right-click -> "Open link in new tab" / "Copy link address"
   *   - any page load where this script fails (blocked, error, slow asset)
   * In all of those the customer reaches the legacy host, which still
   * answers 200 and serves an old funnel whose orders may never land in the
   * current system.
   *
   * So we don't just intercept the click, we replace the destination: every
   * legacy href becomes FUNNEL_URL, and the element is stamped
   * `data-scribbly-funnel` so findFunnelTrigger still recognizes it after
   * the href no longer matches a pattern. A plain click keeps opening the
   * modal exactly as before; every bypass path now lands on the real funnel
   * instead of the dead host.
   *
   * Stamp BEFORE rewriting so the element is never briefly unrecognizable
   * to the click handler.
   *
   * Naturally idempotent: a rewritten href stops matching the patterns, so
   * later passes skip it. This is also what keeps the observer from
   * looping -- FUNNEL_URL matches no pattern, so the second pass is a
   * no-op. There is a test pinning that.
   *
   * This covers pages we can't edit from this repo -- the homepage and
   * product-page CTAs live in Shopify-Magic-generated theme blocks, not in
   * `shopify/`. It does NOT help a crawler or a visitor with JS disabled;
   * those still need the blocks edited in the Shopify admin.
   */
  function rewriteLegacyFunnelLinks() {
    var anchors = document.querySelectorAll('a[href]');
    for (var i = 0; i < anchors.length; i++) {
      var a = anchors[i];
      if (!matchesFunnelPattern(a.getAttribute('href'))) continue;
      a.setAttribute('data-scribbly-funnel', '');
      a.setAttribute('href', FUNNEL_URL);
    }
    // Shopify Magic blocks sometimes wire a CTA through data-link/data-href
    // and navigate with their own JS. Same exposure if our click handler
    // never runs, so same treatment. A button can carry both attributes.
    var btns = document.querySelectorAll('button[data-link], button[data-href]');
    for (var j = 0; j < btns.length; j++) {
      var btn = btns[j];
      var stamped = false;
      var attrs = ['data-link', 'data-href'];
      for (var k = 0; k < attrs.length; k++) {
        if (!matchesFunnelPattern(btn.getAttribute(attrs[k]))) continue;
        if (!stamped) {
          btn.setAttribute('data-scribbly-funnel', '');
          stamped = true;
        }
        btn.setAttribute(attrs[k], FUNNEL_URL);
      }
    }
  }

  /** Find the closest funnel-triggering ancestor of the click target.
   *  Resolution order (first match wins):
   *    1. [data-scribbly-funnel]                  -- explicit opt-in
   *    2. <a href="...wlcmapps.com/order-steps...">  -- auto-detected legacy CTA
   *    3. <button data-link|data-href="...">      -- Shopify Magic blocks
   *       that wire the CTA via JS rather than a real href
   *  Returns null if none of the above match. */
  function findFunnelTrigger(el) {
    if (!el || !el.closest) return null;
    var explicit = el.closest('[data-scribbly-funnel]');
    if (explicit) return explicit;
    var anchor = el.closest('a[href]');
    if (anchor && matchesFunnelPattern(anchor.getAttribute('href'))) return anchor;
    var btn = el.closest('button[data-link], button[data-href]');
    if (btn) {
      var link = btn.getAttribute('data-link') || btn.getAttribute('data-href') || '';
      if (matchesFunnelPattern(link)) return btn;
    }
    return null;
  }

  /** Single delegated click handler — survives any DOM the theme adds
   *  later (e.g. dynamically-rendered product cards from app blocks)
   *  and works on AI-generated blocks where we can't paste attributes. */
  document.addEventListener('click', function (e) {
    var target = findFunnelTrigger(e.target);
    if (!target) return;
    // For real anchors we MUST preventDefault — otherwise the browser
    // starts navigating to the legacy URL while our modal opens, and the
    // tab flickers white before the iframe loads. stopPropagation keeps
    // any theme-level link tracking from also firing on the dead click.
    e.preventDefault();
    e.stopPropagation();

    // Cap gate. If the customer already has MAX_KIDS in-flight resume
    // cards, refuse to open the funnel and surface a friendly toast
    // instead. Without this, starting a 7th preview would silently evict
    // the oldest card from scribbly:resume, stranding that kid's
    // preview. We gate on EVERY funnel-trigger click (general AND
    // per-product) because the launcher can't tell from the click alone
    // whether the per-product flow will pick an existing saved kid or
    // create a new one — the customer's path to resolution (dismiss a
    // card or finish a preview) is the same either way.
    //
    // Use loadAll() (returns ResumeRecord[]) NOT loadSaved() — the
    // latter returns a single record (the newest), and reading .length
    // off a record object silently produced undefined, so the original
    // PR shipped a no-op gate. Caught during the live E2E on
    // 2026-05-12.
    var saved = loadAll();
    if (saved.length >= MAX_KIDS) {
      showCapToast();
      return;
    }

    lastOpener = target;
    // `data-book-handle` (the Shopify product handle, e.g. "cloud-school")
    // marks per-product "Preview your book" CTAs. Without it, the click
    // is the general "Preview your book" CTA and runs the kid-first flow
    // exactly as today.
    open({
      occasion: target.getAttribute('data-occasion') || null,
      bookHandle: target.getAttribute('data-book-handle') || null,
    });
  });

  /** postMessage listener — funnel → parent control channel.
   *  Origin check above; payload shapes:
   *    { type: 'scribbly:close' }
   *    { type: 'scribbly:close', resume: { slug, childName } }      <- from bookshop
   *    { type: 'scribbly:close', resume: { leadId, childName } }    <- mid-funnel
   *    { type: 'scribbly:resume_update', resume: { leadId, … } }    <- live pill sync
   *    { type: 'scribbly:resume_snapshot', resume: { leadId, … } }  <- proactive localStorage
   *    { type: 'scribbly:checkout', occasion: string|null, slug: string|null }
   *    { type: 'scribbly:overlay', state: 'open'|'closed' }         <- bookshop
   *      dedication upsell. Stretches the funnel stage to fill the
   *      browser viewport so the in-iframe .bs-overlay scrim spans the
   *      whole window instead of the modal-sized rectangle.
   */
  window.addEventListener('message', function (e) {
    if (!isAllowedOrigin(e.origin)) return;
    var data = e.data;
    if (!data || typeof data !== 'object' || typeof data.type !== 'string') return;

    if (data.type === 'scribbly:close') {
      // Persist resume info BEFORE closing so the floating card paints on
      // the very next page interaction (the close() blanks the iframe but
      // doesn't reload the storefront). saveResume validates shape — both
      // bookshop {slug, childName} and generating {leadId, childName} land
      // in the same localStorage row (last-write-wins).
      if (data.resume && typeof data.resume === 'object') {
        saveResume(data.resume);
        renderResumeCard();
      }
      close();
    } else if (data.type === 'scribbly:checkout') {
      // Redirect the top window to the named bookshop on the SAME origin
      // the funnel iframe is running on — so prod-funnel → prod-bookshop,
      // preview-funnel → preview-bookshop, no env mismatch. Fall back to
      // closing the modal if we somehow got here without a slug (older
      // funnel build, or the customer hit Bookshop before the lead saved).
      if (typeof data.slug === 'string' && /^[a-z0-9]{1,24}-[a-z0-9]{6}$/.test(data.slug)) {
        window.top.location.href = e.origin + '/shop/' + data.slug;
      } else {
        close();
      }
    } else if (data.type === 'scribbly:overlay') {
      // Toggle a class on the funnel root so its CSS expands the stage to
      // fill the browser. Used by the bookshop dedication upsell so its
      // own .bs-overlay scrim isn't trapped inside the modal-sized iframe.
      if (rootEl) {
        if (data.state === 'open') {
          rootEl.classList.add('is-overlay-active');
        } else {
          rootEl.classList.remove('is-overlay-active');
        }
      }
    } else if (data.type === 'scribbly:save_kid') {
      // Funnel just finished a kid's preview. Append to the customer's
      // saved-kids list (per-device localStorage on the storefront
      // origin) so the next time they click "Preview your book" on a
      // different product, the launcher can hand the funnel a
      // `?kids=...` list and skip the photo upload flow.
      if (data.kid && typeof data.kid === 'object') {
        appendSavedKid(data.kid);
      }
    } else if (data.type === 'scribbly:resume_update' || data.type === 'scribbly:resume_snapshot') {
      // Live sync / proactive snapshot while the iframe stays open — same
      // payload shape as close's `resume` field. Paint or refresh the pill
      // without dismissing the modal. Snapshot survives browser refresh
      // during generation when close never fires.
      if (data.resume && typeof data.resume === 'object') {
        saveResume(data.resume);
        renderResumeStack();
      }
    }
  });

  // Expose a tiny imperative API for theme code that wants to open the
  // modal programmatically (e.g. from an app block, or after a delay).
  window.ScribblyFunnel = { open: open, close: close };

  /* ─── Product-card Add-to-Cart → Preview transformer ───
   *
   * Every Scribbly book MUST be previewed before purchase — without a
   * preview the order has no portrait id and fulfillment can't attach a
   * personalized illustration. The product-page CTA is handled by the
   * `templates/product.preview.json` alternate template, but Shopify
   * themes also surface "Add to Cart" buttons on product CARDS across
   * the site:
   *   - Collection / category pages (grid views)
   *   - Search results
   *   - "Related products" / "You may also like" sections on PDPs
   *   - Featured products on the homepage
   * Each of those quick-add buttons is a path to checkout that bypasses
   * the funnel — same fulfillment-breaking outcome as the main PDP form
   * before we replaced that.
   *
   * This transformer rewrites every Add-to-Cart button on a product
   * card: relabels it to "Preview your book", strips `name="add"` so
   * the parent /cart/add form doesn't capture clicks, and stamps
   * `data-scribbly-funnel` + `data-book-handle` so the existing
   * delegated click handler routes the click through the funnel.
   *
   * Cart-drawer Add-to-Cart buttons (Shopify's built-in slide-out cart)
   * are intentionally LEFT ALONE — they're needed for the cart UI to
   * function (e.g. quantity-step buttons sometimes share the selector
   * pattern) and they're not on a product card.
   *
   * Idempotent: re-runs skip already-transformed buttons. MutationObserver
   * picks up cards added by AJAX / lazy-load / app blocks.
   */
  var CARD_BTN_SELECTOR = 'button[name="add"]';
  /* The product page's sticky cart bar is a SECOND Add-to-Cart shape, and
   * the card selector above never matches it: it's <button type="button">
   * with no name attribute, not inside a /cart/add form, wired up purely by
   * the theme's JS off this data attribute. Found live 2026-08-23 on
   * /products/dreamland-book after the card fix — the most dangerous one
   * left, since it follows the customer down the page of a book they're
   * reading about. */
  var CART_BAR_BTN_SELECTOR = '[data-cart-bar-add-to-cart]';
  // Selectors for Shopify's slide-out cart drawer — buttons inside these
  // ancestors are NOT transformed. Tested against Relay's drawer
  // (sections--…__cart-drawer); broader selectors guard other themes.
  var CART_DRAWER_ANCESTOR_SELECTOR = '[id*="cart-drawer"], [class*="cart-drawer"], [id*="cart-modal"], cart-drawer, cart-notification';
  // How far up from the button we'll look for the card's product link.
  // Relay needs 3-4 (see findBookHandle); the cap stops a runaway walk
  // reaching a grid container full of unrelated products.
  var HANDLE_SEARCH_MAX_HOPS = 6;

  /* Find the product handle for a quick-add button by walking UP from the
   * button until an ancestor's subtree contains a /products/ link.
   *
   * This replaced `btn.closest(CARD_ROOT_SELECTOR)`, which looked for one
   * named card-root ancestor and read the link from inside it. On Relay
   * (scribbly-books.myshopify.com) that lands on <quick-add-product>,
   * which wraps ONLY the button — the product link is a sibling branch one
   * level higher, under .product-item__info / .product-upsell__content. So
   * the lookup found no link and every quick-add button on the homepage and
   * the Bookshop collection kept its "Add to cart" label. Reported
   * 2026-08-23.
   *
   * Walking up instead of naming ancestors means we don't have to keep a
   * list of theme-specific card classes in sync with the theme.
   *
   * Ambiguity guard: the first ancestor that contains any product link must
   * contain exactly ONE distinct handle. More than one means we've walked
   * past this card into a grid of several, where picking the first link
   * would send the customer to preview the WRONG book — worse than a
   * generic preview. In that case return null and let the caller open the
   * funnel without a book. Verified against the live theme: all 8 quick-add
   * buttons on the homepage and all 8 on the Bookshop collection resolve to
   * exactly one handle, at 3-4 hops.
   */
  function findBookHandle(btn) {
    var node = btn;
    for (var hops = 0; node && node !== document.body && hops <= HANDLE_SEARCH_MAX_HOPS; hops++) {
      var links = node.querySelectorAll ? node.querySelectorAll('a[href*="/products/"]') : [];
      if (links.length) {
        var handles = [];
        for (var i = 0; i < links.length; i++) {
          var m = (links[i].getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
          if (m && handles.indexOf(m[1]) === -1) handles.push(m[1]);
        }
        // Exactly one book in scope → confident. Otherwise stop looking.
        return handles.length === 1 ? handles[0] : null;
      }
      node = node.parentElement;
    }
    return null;
  }

  /* Relabel + neutralize the product page's sticky cart-bar button.
   *
   * Cloning rather than just restamping: the theme binds its add-to-cart
   * behaviour to this element directly, and attributes alone can't unbind a
   * live listener — a restamped button would open the funnel AND add the
   * book to the cart. replaceWith(cloneNode) drops every listener, and the
   * funnel's own click handler is delegated on document so it still picks
   * the clone up. Verified against the live theme: no /cart/add request
   * fires on click after this runs.
   *
   * The handle comes from <product-info data-product-handle>, which wraps
   * the bar; findBookHandle's link walk is the fallback for themes that
   * don't provide it.
   */
  var PREVIEW_LABEL = 'Preview your book';

  /* Relabel a button for the funnel — visible text AND accessible name.
   *
   * aria-label matters more than it looks: Relay ships these quick-add
   * buttons with aria-label="Add to cart", and an aria-label OVERRIDES the
   * visible text for assistive tech. Setting only textContent left a button
   * that read "Preview your book" on screen while a screen reader still
   * announced "Add to cart" — and then opened the funnel, so the spoken
   * label actively lied about what the button does. Caught on the live
   * search page 2026-08-23, after the visible-text relabel had shipped.
   *
   * Only overwrite an existing aria-label; adding one where the theme had
   * none would just duplicate the visible text to screen readers. */
  function applyPreviewLabel(btn) {
    // Some quick-add buttons hold an icon + text in nested spans;
    // overwriting textContent collapses both into the new label, which is
    // what we want — the icon was a cart icon.
    btn.textContent = PREVIEW_LABEL;
    if (btn.hasAttribute('aria-label')) btn.setAttribute('aria-label', PREVIEW_LABEL);
    // Relay also mirrors the label into a title tooltip on some cards.
    if (btn.hasAttribute('title')) btn.setAttribute('title', PREVIEW_LABEL);
  }

  /* Sever every route a button has back into the theme's add-to-cart, and
   * return the element the funnel should own.
   *
   * Relabelling alone was not enough, and this is the bug that reached
   * customers: the card buttons kept `data-add-to-cart` (the theme's own JS
   * hook) and stayed bound to a /cart/add form through their `form`
   * attribute. So tapping "Preview your book" added the book to the cart
   * and slid the cart drawer open — a button that read Preview and did
   * Add-to-Cart. Reported by Lindsey 2026-08-23.
   *
   * Removing name="add" only stops native form submission. It does nothing
   * about (a) a delegated/direct listener keyed on data-add-to-cart, or
   * (b) the `form` attribute, which associates the button with a form
   * ANYWHERE in the document, not just an ancestor.
   *
   * So: clone the node (drops every attached listener — attributes can't
   * unbind a live one), strip the theme's hooks and the form association,
   * then hand back the clone. The funnel's own click handler is delegated
   * on document, so it picks up the replacement.
   */
  var THEME_ADD_HOOKS = ['data-add-to-cart', 'data-cart-bar-add-to-cart', 'data-quick-add-btn'];

  function detachFromTheme(btn) {
    var fresh = btn.cloneNode(false);
    for (var i = 0; i < THEME_ADD_HOOKS.length; i++) fresh.removeAttribute(THEME_ADD_HOOKS[i]);
    // `form` re-attaches a button to a /cart/add form from anywhere in the
    // document; type=button stops a native submit either way, but the
    // theme's JS also reads this to find the form to post.
    fresh.removeAttribute('form');
    fresh.removeAttribute('name');
    fresh.setAttribute('type', 'button');
    btn.replaceWith(fresh);
    return fresh;
  }

  function transformCartBarButtons() {
    var bars = document.querySelectorAll(CART_BAR_BTN_SELECTOR);
    for (var i = 0; i < bars.length; i++) {
      var btn = bars[i];
      if (btn.hasAttribute('data-scribbly-funnel')) continue;
      if (btn.closest(CART_DRAWER_ANCESTOR_SELECTOR)) continue;
      var holder = btn.closest('[data-product-handle]');
      var handle = (holder && holder.getAttribute('data-product-handle')) || findBookHandle(btn);
      var fresh = detachFromTheme(btn);
      fresh.setAttribute('data-scribbly-funnel', '');
      if (handle) fresh.setAttribute('data-book-handle', handle);
      applyPreviewLabel(fresh);
    }
  }

  function transformProductCardButtons() {
    var btns = document.querySelectorAll(CARD_BTN_SELECTOR);
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      // Skip already-transformed (idempotent across MutationObserver
      // ticks and re-renders).
      if (btn.hasAttribute('data-scribbly-funnel')) continue;
      // Skip cart-drawer buttons — they're not on a product card.
      if (btn.closest(CART_DRAWER_ANCESTOR_SELECTOR)) continue;
      // Find the product handle by walking up to the card root and
      // looking for the first /products/<handle> link inside it.
      var handle = findBookHandle(btn);
      // Transform: relabel + stamp data attrs + neutralize the form submit.
      // A null handle still gets transformed — an untagged "Preview your
      // book" opens the funnel generically, which is strictly better than
      // leaving an Add-to-Cart that breaks fulfillment. Only the deep-link
      // to a specific book is lost.
      var fresh = detachFromTheme(btn);
      fresh.setAttribute('data-scribbly-funnel', '');
      if (handle) fresh.setAttribute('data-book-handle', handle);
      applyPreviewLabel(fresh);
    }
  }

  // Run once at script load (covers DOMContentLoaded + later loads),
  // then on every DOM change so AJAX-loaded sections (collection page
  // pagination, infinite scroll, search filters) get transformed too.
  // The function is cheap (one querySelectorAll across document) and
  // idempotent, so re-running on every mutation tick is fine for the
  // small DOM sizes Shopify storefronts have. If perf ever becomes a
  // concern, throttle via requestAnimationFrame.
  //
  // Both DOM passes share one entry point and one observer: they scan the
  // same document at the same moments, and rewriting hrefs early matters
  // for the same reason relabeling cards does.
  function runDomPasses() {
    rewriteLegacyFunnelLinks();
    transformProductCardButtons();
    transformCartBarButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runDomPasses);
  } else {
    runDomPasses();
  }
  if (typeof MutationObserver !== 'undefined') {
    var cardObserver = new MutationObserver(runDomPasses);
    cardObserver.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  /* ─── Saved kids ───
   *
   * Per-device localStorage list of kids the customer has previewed.
   * Powers the ChooseKid screen on the per-product "Preview your book"
   * CTA — a returning customer with saved kids can pick one and skip
   * the entire photo/name/eye/age/contact pipeline for the new book.
   *
   * Storage shape: { v: 1, kids: [{ slug, name, savedAt }, ...] }
   *
   * - Newest first; older entries pushed down.
   * - De-duped by slug — re-saving an existing kid bumps it to the top
   *   without creating a duplicate row.
   * - Capped at MAX_SAVED_KIDS so a customer who's previewed dozens of
   *   kids doesn't bloat the URL when the launcher passes `?kids=...`.
   * - Read inline in `open()` (no caching here — the file is small and
   *   localStorage reads are cheap).
   */
  var SAVED_KIDS_KEY = 'pf:my_kids';
  var MAX_SAVED_KIDS = 6;

  function readSavedKids() {
    try {
      var raw = window.localStorage.getItem(SAVED_KIDS_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.kids)) return [];
      return parsed.kids.filter(function (k) {
        return k && typeof k.slug === 'string' && typeof k.name === 'string';
      });
    } catch (e) {
      return [];
    }
  }

  // The funnel now mints a per-lead portrait_token at INSERT and persists
  // it in localStorage on the FUNNEL origin (under pf:portrait_token:{leadId}).
  // After portrait-token gating landed on /api/bookshop-*, the ChooseKid
  // screen has to send X-Scribbly-Portrait-Token on every kid's
  // bookshop-session fetch — otherwise the endpoint returns the "locked"
  // shape and the picker drops every saved kid. To make that lookup
  // possible, each saved-kid record now also carries `leadId` (the row
  // UUID of the kid's lead). The funnel reads `?kids=...&leadIds=...` at
  // mount and threads leadIds down to ChooseKid; ChooseKid uses each
  // leadId to look up the matching portrait_token in (funnel-origin)
  // localStorage. Legacy records written before this fix don't have a
  // leadId — they fail the gate and get filtered out (degraded but
  // graceful — see the project notes on the legacy-entry trade-off).
  function appendSavedKid(kid) {
    if (!kid || typeof kid.slug !== 'string' || typeof kid.name !== 'string') return;
    var slug = kid.slug.trim();
    var name = kid.name.trim();
    if (!slug || !name) return;
    // leadId is optional on the wire (older funnel builds didn't send it)
    // — when present we validate UUID shape defensively because the
    // postMessage payload is cross-origin and not implicitly trusted.
    var leadId = null;
    if (typeof kid.leadId === 'string' && UUID_RE.test(kid.leadId)) {
      leadId = kid.leadId;
    }
    var existing = readSavedKids();
    var deduped = existing.filter(function (k) { return k.slug !== slug; });
    var record = { slug: slug, name: name, savedAt: Date.now() };
    if (leadId) record.leadId = leadId;
    var next = [record].concat(deduped);
    if (next.length > MAX_SAVED_KIDS) next = next.slice(0, MAX_SAVED_KIDS);
    try {
      window.localStorage.setItem(
        SAVED_KIDS_KEY,
        JSON.stringify({ v: 1, kids: next }),
      );
    } catch (e) {
      // localStorage can throw (private mode, quota). Silent — the
      // ChooseKid flow is an enhancement, not a load-bearing feature.
    }
  }

  /* ─── Resume card ───
   *
   * Surfaces a floating card on the storefront after the customer closes
   * the funnel modal, so they can hop back in without re-running the funnel.
   * Two kinds:
   *
   *   bookshop:   Customer left from /shop/<slug>. Card says "Continue
   *               browsing {Name}'s Bookshop" → reopens the modal at the
   *               bookshop URL.
   *   generating: Customer left mid-pipeline. Card shows the same green
   *               "Illustrating {Name}" progress bar the funnel renders
   *               internally, polls /api/preview-leads for status, flips
   *               to "ready!" when illustration_url is populated.
   *
   * Card lives until the customer dismisses it or it ages out
   * (RESUME_TTL_MS). Single localStorage key — last-write-wins.
   */
  var RESUME_KEY = 'scribbly:resume';
  var LEGACY_BOOKSHOP_KEY = 'scribbly:resume-bookshop'; // PR #190 key — migrated on first read
  var RESUME_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours (founder UX spec 2026-06-29)

  // localStorage payload shape:
  //   v1 (legacy): { kind, leadId|slug, childName, savedAt, pipelineStatus, photoUrl, ... }
  //                — single record. loadSaved migrates this on first read.
  //   v2 (current): { v: 2, kids: [<record>, ...] }
  //                — up to MAX_KIDS records. Newest first; oldest evicted on overflow.
  // Each record's identity:
  //   - generating kind: keyed by leadId
  //   - bookshop kind:   keyed by slug
  // We allow both kinds to coexist in `kids` so a customer with two kids
  // (one mid-pipeline, one finished) sees two cards simultaneously.
  var RESUME_SCHEMA_VERSION = 2;
  // Max in-progress / finished-but-not-purchased preview sessions a single
  // device can have at once. Raised from 5 → 6 in PR #?? so the 6th kid no
  // longer silently evicts the oldest. The launcher's delegated click
  // handler refuses to open the funnel when scribbly:resume.length >= cap,
  // surfacing the cap toast instead (see showCapToast below) so the
  // customer dismisses or finishes a card before starting another.
  var MAX_KIDS = 6;

  // Slug shape mirrors src/bookshop/slug.ts CHILD_SLUG_REGEX. UUID shape
  // mirrors api/preview-leads.ts UUID_RE. We re-validate defensively because
  // localStorage is user-writable.
  var SLUG_RE = /^[a-z0-9]{1,24}-[a-z0-9]{6}$/;
  var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // Generation-progress polling.
  var POLL_INTERVAL_MS = 5000;        // every 5s while card is visible + tab focused
  var POLL_HARD_CAP_MS = 10 * 60 * 1000; // give up after 10 min — SMS/email will catch them

  // Pipeline-progress math — DUPLICATED FROM src/preview-funnel/pipelineProgress.ts.
  // Update both files together if you change the bands. The funnel and this
  // parent script both render the same green bar; keeping the math identical
  // means the bar tracks smoothly across the close → resurface boundary.
  var PIPELINE_BANDS = {
    idle:               [0,    0.1],
    // Profile not finished — generation never started. No bar band is
    // ever painted for this status (its card variant has no bar); it
    // lives here so the funnel-forwarded close status validates.
    profile_incomplete: [0,    0],
    p1_running:         [0.1,  0.5],
    p1_done:            [0.5,  0.5],
    p2_running:         [0.5,  0.9],
    rendering_bookshop: [0.9,  0.99],
    done:               [1,    1],
    failed:             [0,    0]
  };
  var STORY_SLIDE_MS = 5000;          // mirrors STORY_SLIDE_MS in pipelineProgress.ts
  var STORY_SLIDE_COUNT = 12;         // mirrors SLIDES.length in screens/Waiting.tsx

  function pipelineProgress(phase, slideIndex, totalSlides) {
    if (phase === 'done') return 1;
    if (phase === 'failed') return 0;
    var band = PIPELINE_BANDS[phase] || PIPELINE_BANDS.idle;
    var lo = band[0], hi = band[1];
    if (lo === hi) return lo;
    var slidePct = totalSlides > 1
      ? Math.min(1, Math.max(0, slideIndex / (totalSlides - 1)))
      : 0;
    return lo + (hi - lo) * slidePct;
  }

  // Time-calibrated progress (poster-style) — MIRRORS pipelineProgressTimed
  // in src/preview-funnel/pipelineProgress.ts. Update both files together.
  // The slide-index crawl above is decoupled from real timing, so the bar
  // could sit near-full while the pipeline was still working. This variant
  // ramps smoothly toward a ceiling calibrated to the real preview pipeline
  // duration and HOLDS there until the poll's independent "done" signal
  // lands, then snaps to 100% — the same logic the posters use.
  var PROGRESS_HOLD_CEILING = 0.92;     // mirrors PROGRESS_HOLD_CEILING
  // mirrors EXPECTED_TOTAL_MS. Retuned 2026-05-19 from 16000→75000 when
  // P4 single-shot was turned off and the funnel reverted to P1→P2 (real
  // wall clock ~70–90s). At 16s the bar hit 92% in 14s and held for over
  // a minute — looked frozen on the resume card just as much as in the
  // funnel itself. Update src/preview-funnel/pipelineProgress.ts in lockstep.
  var EXPECTED_TOTAL_MS = 75000;
  var DONE_SNAP_MS = 800;               // mirrors DONE_SNAP_MS
  // Past this elapsed, a still-in-flight generating card stops showing a
  // (held-at-92%) bar and switches to the calm "Stand by" message — a book
  // running well past the ~75s norm reads as stuck, not progressing.
  // (Founder UX spec 2026-06-29, decision B.)
  var STANDBY_AFTER_MS = 3 * 60 * 1000; // 3 min

  function pipelineProgressTimed(phase, elapsedMs, doneElapsedMs) {
    if (phase === 'failed') return 0;
    if (phase === 'done') {
      var de = (typeof doneElapsedMs === 'number' && isFinite(doneElapsedMs))
        ? Math.max(0, doneElapsedMs)
        : DONE_SNAP_MS;
      return Math.min(
        1,
        PROGRESS_HOLD_CEILING + (1 - PROGRESS_HOLD_CEILING) * (de / DONE_SNAP_MS)
      );
    }
    var e = Math.max(0, elapsedMs);
    return Math.min(PROGRESS_HOLD_CEILING, PROGRESS_HOLD_CEILING * e / EXPECTED_TOTAL_MS);
  }

  function formatElapsed(ms) {
    var s = Math.floor(Math.max(0, ms) / 1000);
    var m = Math.floor(s / 60);
    var ss = s % 60;
    return m + ':' + (ss < 10 ? '0' : '') + ss;
  }

  // Elapsed since generation began. Prefer the authoritative server start
  // (pipelineStartedAt, backfilled by pollOne) so the bar + counter stay
  // correct even when the resume card surfaces long after the customer
  // closed the funnel. Fall back to savedAt (close time — slightly behind
  // reality, never ahead, so still honest), then to "now".
  function recordElapsedMs(rec) {
    var start = null;
    if (rec && typeof rec.pipelineStartedAt === 'string') {
      var t = Date.parse(rec.pipelineStartedAt);
      if (!isNaN(t)) start = t;
    }
    if (start === null && rec && typeof rec.savedAt === 'number') start = rec.savedAt;
    if (start === null) start = Date.now();
    return Math.max(0, Date.now() - start);
  }

  // Ms since the record flipped to "done" (readyAt is stamped by pollOne on
  // that transition) — drives the ceiling→100% snap. Infinity until then.
  function recordDoneElapsedMs(rec) {
    if (rec && typeof rec.readyAt === 'number') return Math.max(0, Date.now() - rec.readyAt);
    return Infinity;
  }

  function nameOK(name) { return typeof name === 'string' && name.length > 0; }
  function trimName(name) { return name.slice(0, 60); }

  // Validate the photo URL before persisting/rendering. Storage URLs the
  // funnel writes look like https://<project>.supabase.co/storage/v1/...
  // We accept any https URL but cap length and refuse anything else (data:,
  // javascript:, http:) since this string ends up in an <img src>.
  function photoUrlOK(url) {
    if (typeof url !== 'string' || url.length === 0 || url.length > 2048) return false;
    return url.indexOf('https://') === 0;
  }

  // photoPathChanged() is defined further down alongside
  // refreshBookshopThumbnails(); the comparator is shared between the
  // resume-card poll (pollOne) and the bookshop thumbnail backfill, and
  // we keep one canonical implementation (the URL-pathname-based one).

  /**
   * Possessive form of a child's first name. Mirrors the React-side
   * src/lib/utils/namePossessive.ts helper that the React app uses to
   * render the bookshop's "{Name}'s Collection" heading. Both must
   * agree so the storefront resume card and the in-modal heading
   * read the same. AP style: trailing-s gets just an apostrophe;
   * everything else gets 's. Whitespace-trimmed, tolerant of trailing
   * punctuation. We use the curly apostrophe (’ / ’s) to match the
   * Figma typography.
   *
   * If you change this rule, update src/lib/utils/namePossessive.ts
   * in the same PR so the two stay in sync.
   *
   * Examples:
   *   "Sophie"  → "Sophie’s"
   *   "Charles" → "Charles’"   (QA-flagged 2026-05-12: was "Charles’s")
   *   "Jess"    → "Jess’"
   *   "CHARLES" → "CHARLES’"   (case-insensitive on trailing letter)
   */
  function namePossessive(name) {
    if (typeof name !== 'string') return '';
    var trimmed = name.trim();
    if (!trimmed) return '';
    var lastAlpha = trimmed.replace(/[^A-Za-z]+$/, '').slice(-1).toLowerCase();
    return lastAlpha === 's' ? trimmed + '’' : trimmed + '’s';
  }

  /** Build a fresh record from a save() input. Returns null if the input
   *  is malformed (no valid leadId/slug/name, etc.). Does not write. */
  function buildRecord(resume) {
    if (!resume || typeof resume !== 'object') return null;
    if (!nameOK(resume.childName)) return null;
    var record = { childName: trimName(resume.childName), savedAt: Date.now() };
    if (photoUrlOK(resume.photoUrl)) record.photoUrl = resume.photoUrl;
    // Per-occasion entry context — preserved so the resume card's Open
    // click can rebuild the funnel URL with these params and the funnel's
    // express-skip logic fires on resume. Validated lightly: occasion is
    // a short ASCII-ish label, bookId is the book_id from
    // bookshop_listings (lowercase kebab+random suffix). express is a
    // boolean flag.
    if (typeof resume.occasion === 'string' && /^[A-Za-z0-9_-]{1,40}$/.test(resume.occasion)) {
      record.occasion = resume.occasion;
    }
    if (resume.express === true) {
      record.express = true;
    }
    if (typeof resume.bookId === 'string' && /^[a-z0-9-]{1,80}$/.test(resume.bookId)) {
      record.bookId = resume.bookId;
    }
    if (typeof resume.slug === 'string' && SLUG_RE.test(resume.slug)) {
      record.kind = 'bookshop';
      record.slug = resume.slug;
      // Capture the leadId on bookshop records too, when the funnel
      // forwards it. The Open click on the resume card uses it to
      // populate the bookshop URL's `#leadId=` fragment so the iframe
      // can find its portrait token in localStorage. Without it the
      // bookshop falls into its degraded "locked" view, which is the
      // right behavior for legacy records saved before this rolled out
      // but not for fresh saves on this device.
      if (typeof resume.leadId === 'string' && UUID_RE.test(resume.leadId)) {
        record.leadId = resume.leadId;
      }
      // Lead edit token (64-hex) — forwarded same-device so the storefront
      // can poll /api/lead-purchased (clear-on-purchase) and refresh the
      // thumbnail via /api/preview-leads. Mirrors the generating branch
      // below. Cross-device deep links omit it; those records degrade
      // gracefully (no poll, dismiss-only — see refreshBookshopThumbnails).
      if (typeof resume.editToken === 'string' && /^[0-9a-f]{64}$/.test(resume.editToken)) {
        record.editToken = resume.editToken;
      }
      return record;
    }
    if (typeof resume.leadId === 'string' && UUID_RE.test(resume.leadId)) {
      record.kind = 'generating';
      record.leadId = resume.leadId;
      // Lead-scoped auth token. Required for cross-origin polls against
      // /api/preview-leads (the storefront and the funnel are different
      // origins, so the storefront can't read the funnel's localStorage).
      // 64-char hex (32 bytes) per api/_lib/edit-token.ts's generate().
      // Stored alongside leadId; lost only if the customer clears the
      // storefront's localStorage. Optional in the payload because the
      // funnel sets it best-effort — a record without a token is still
      // valid for rendering but its poller will 401 and stop refreshing.
      if (typeof resume.editToken === 'string' && /^[0-9a-f]{64}$/.test(resume.editToken)) {
        record.editToken = resume.editToken;
      }
      // Honor the pipelineStatus the funnel forwarded on close (so the
      // bar paints from the customer's actual progress, not a 10% sliver).
      // Any value the funnel sends is validated against PIPELINE_BANDS;
      // unknown values fall back to p1_running. The first poll will
      // overwrite this with the authoritative server status either way.
      var phase = typeof resume.pipelineStatus === 'string' ? resume.pipelineStatus : null;
      record.pipelineStatus =
        phase && Object.prototype.hasOwnProperty.call(PIPELINE_BANDS, phase)
          ? phase
          : 'p1_running';
      // Honor the server's pipeline_started_at when the funnel forwards
      // it on close. Without this the pill anchors recordElapsedMs() to
      // savedAt (close time) for the first ~5s until pollOne backfills
      // from the lead row — paints the bar at ~0% on a customer who was
      // mid-pipeline. Validated as a parseable ISO date no later than now.
      if (typeof resume.pipelineStartedAt === 'string') {
        var startedMs = Date.parse(resume.pipelineStartedAt);
        if (!isNaN(startedMs) && startedMs > 0 && startedMs <= Date.now()) {
          record.pipelineStartedAt = resume.pipelineStartedAt;
        }
      }
      return record;
    }
    return null; // neither slug nor leadId valid
  }

  /** Stable identity for a record. Two saves with the same key target the
   *  same kid (e.g. a generating save followed by a bookshop save when the
   *  same kid's pipeline completes). */
  function recordKey(record) {
    if (!record) return null;
    if (record.kind === 'bookshop') return 'slug:' + record.slug;
    if (record.kind === 'generating') return 'lead:' + record.leadId;
    return null;
  }

  /** Cross-kind identity for a record. Bookshop records are keyed by slug;
   *  generating records are keyed by leadId. Those keys never align even
   *  for the same physical kid, so to dedupe across kinds we compare on
   *  data the parent JS actually sees: childName + photoUrl.
   *
   *  - childName must match (case-insensitive, trimmed).
   *  - If both records have a photoUrl, those must also match — compared
   *    by URL PATHNAME (NOT full URL) so two signed Supabase URLs for
   *    the same underlying object compare equal even though their
   *    `?token=&exp=` query strings differ. The funnel signs photos via
   *    /api/signed-image-url and the bookshop signs via /api/preview-leads
   *    at different moments → different signatures, identical paths.
   *    Strict full-URL equality (the pre-fix behavior) silently failed
   *    to dedup the same kid across the two surfaces; the leadId-based
   *    save-time guard in `saveResume` covers the dominant case but the
   *    render-time filter `isRedundantInFlightGenerating` still routes
   *    through here, so this fix gives defense in depth.
   *  - This guards against twins-with-the-same-name: photo PATHS are
   *    per-kid (preview-funnel/<leadId>/photo.jpeg), so two distinct
   *    leadIds produce distinct paths and the dedup correctly does NOT
   *    fire for twins.
   *  - If either side lacks a photoUrl, fall back to childName-only. The
   *    photoUrl-less record is normally the older / less complete one
   *    (e.g. a generating record saved before photo upload finished),
   *    and the name alone is enough confidence in that case.
   *  - Malformed URLs (no protocol, weird shapes) fall back to full-string
   *    equality. Conservative: if we can't parse, we'd rather miss a
   *    legitimate dedup than incorrectly merge two unrelated records.
   *    Mirror of `photoPathChanged()` (line ~1513) which uses the same
   *    URL-parse + fallback pattern for the inverse direction.
   */
  function sameKid(a, b) {
    if (!a || !b) return false;
    if (!nameOK(a.childName) || !nameOK(b.childName)) return false;
    var na = String(a.childName).trim().toLowerCase();
    var nb = String(b.childName).trim().toLowerCase();
    if (na !== nb) return false;
    var pa = photoUrlOK(a.photoUrl) ? a.photoUrl : null;
    var pb = photoUrlOK(b.photoUrl) ? b.photoUrl : null;
    if (pa && pb) {
      if (pa === pb) return true; // identical (including any query strings)
      try {
        return new URL(pa).pathname === new URL(pb).pathname;
      } catch (e) {
        return false; // parse failed AND strict-equals already false above
      }
    }
    return true; // name match + at least one side missing photoUrl
  }

  /** Validate a single record against the schema. Returns true if it can be
   *  rendered safely; false if it should be dropped (malformed, expired). */
  function recordValid(data) {
    if (!data || typeof data !== 'object') return false;
    if (typeof data.savedAt !== 'number') return false;
    if (Date.now() - data.savedAt > RESUME_TTL_MS) return false;
    if (!nameOK(data.childName)) return false;
    if (data.kind === 'bookshop') {
      return typeof data.slug === 'string' && SLUG_RE.test(data.slug);
    }
    if (data.kind === 'generating') {
      return typeof data.leadId === 'string' && UUID_RE.test(data.leadId);
    }
    return false;
  }

  /** Read the raw v2 envelope from localStorage, migrating from v1 / legacy
   *  bookshop key on first read. Returns an array of valid records (newest
   *  first) — empty array if nothing usable. Drops malformed/expired entries
   *  and rewrites the cleaned set so subsequent reads stay fast. */
  function loadAll() {
    if (typeof localStorage === 'undefined') return [];
    var raw;
    try { raw = localStorage.getItem(RESUME_KEY); } catch (e) { return []; }

    // Migration step 1 — the PR #190 single-purpose bookshop key. Older
    // browsers may still have this; convert it into the v2 envelope on first
    // read, then drop it. Only fires when the new key is empty.
    if (!raw) {
      var legacyRaw;
      try { legacyRaw = localStorage.getItem(LEGACY_BOOKSHOP_KEY); } catch (e) { return []; }
      try { localStorage.removeItem(LEGACY_BOOKSHOP_KEY); } catch (e) { /* ignore */ }
      if (legacyRaw) {
        try {
          var legacy = JSON.parse(legacyRaw);
          if (legacy && typeof legacy === 'object' && legacy.slug && legacy.childName) {
            var migrated = buildRecord({ slug: legacy.slug, childName: legacy.childName });
            if (migrated) writeAll([migrated]);
          }
        } catch (e) { /* malformed — drop it */ }
        try { raw = localStorage.getItem(RESUME_KEY); } catch (e) { return []; }
      }
      if (!raw) return [];
    }

    var data;
    try { data = JSON.parse(raw); } catch (e) { writeAll([]); return []; }

    // Migration step 2 — v1 single-record format. The old shape was a flat
    // record without a `v` field. Wrap it into the v2 envelope.
    if (data && typeof data === 'object' && !Array.isArray(data) && data.v !== RESUME_SCHEMA_VERSION) {
      if (recordValid(data)) {
        writeAll([data]);
        return [data];
      }
      // Has neither valid v1 fields nor v2 envelope — clear and start over.
      writeAll([]);
      return [];
    }

    if (!data || typeof data !== 'object' || !Array.isArray(data.kids)) {
      writeAll([]);
      return [];
    }

    // v2 envelope — filter out any expired/malformed records and persist the
    // cleaned set so we don't re-validate them on every read.
    var clean = [];
    for (var i = 0; i < data.kids.length; i += 1) {
      if (recordValid(data.kids[i])) clean.push(data.kids[i]);
    }
    if (clean.length !== data.kids.length) writeAll(clean);
    return clean;
  }

  /** Write the array back as a v2 envelope. Caller must have already
   *  validated the records — we trust input here. */
  function writeAll(kids) {
    if (typeof localStorage === 'undefined') return;
    try {
      if (!kids || kids.length === 0) {
        localStorage.removeItem(RESUME_KEY);
        return;
      }
      var envelope = { v: RESUME_SCHEMA_VERSION, kids: kids };
      localStorage.setItem(RESUME_KEY, JSON.stringify(envelope));
    } catch (e) { /* private mode / quota — degrade gracefully */ }
  }

  /** Append-or-update a kid record in the array, keyed by recordKey().
   *  Newest-first ordering (the saved kid moves to position 0). When the
   *  cap is exceeded, oldest entries are evicted from the tail.
   *
   *  Cross-tab merge: localStorage isn't atomic and two storefront tabs
   *  closing modals near-simultaneously will each see the same "pre-write"
   *  state, compute a next array independently, and the second writer
   *  clobbers the first. We re-read just before writing and merge any
   *  new keys from the concurrent tab into our next array. The remaining
   *  race window (between the merge-read and writeAll) is a few
   *  microseconds — small enough to ignore in practice, but a `storage`
   *  event listener below catches anything that does sneak past. */
  function saveResume(resume) {
    var record = buildRecord(resume);
    if (!record) return;
    var key = recordKey(record);
    var current = loadAll();
    // Don't let a second close overwrite a poll-confirmed "done" with a
    // stale in-flight phase from the iframe's local pipeline state.
    if (record.kind === 'generating' && record.pipelineStatus !== 'done') {
      for (var p = 0; p < current.length; p += 1) {
        var prev = current[p];
        if (recordKey(prev) !== key) continue;
        if (prev.kind === 'generating' && prev.pipelineStatus === 'done') return;
      }
    }
    var next = [];
    for (var i = 0; i < current.length; i += 1) {
      if (recordKey(current[i]) !== key) next.push(current[i]);
    }
    next.unshift(record); // newest first
    // Re-read storage just before write. If another tab has written kids
    // we don't have, merge them in (preserve their data — they may be
    // newer than our snapshot from the top of the function). Same key
    // wins for our local record (we just upserted it).
    var concurrent = loadAll();
    for (var j = 0; j < concurrent.length; j += 1) {
      var ckey = recordKey(concurrent[j]);
      var found = false;
      for (var k = 0; k < next.length; k += 1) {
        if (recordKey(next[k]) === ckey) { found = true; break; }
      }
      if (!found) next.push(concurrent[j]);
    }

    // Save-time dedupe: a bookshop record supersedes any generating
    // record for the same kid. Once the customer reaches the bookshop,
    // the funnel-resume "Meet character" card is stale — they've
    // already seen Reveal. Lindsey QA, 2026-05-07.
    //
    // Forward-only: a generating record arriving while a bookshop
    // record exists means the customer is starting a fresh funnel
    // run for the same kid (e.g. redoing with a different photo).
    // Both should remain until that new run finishes and writes a
    // replacement bookshop record (same slug = same recordKey).
    //
    // Runs AFTER the cross-tab merge so a generating record re-merged
    // in from a concurrent writer is also filtered out.
    if (record.kind === 'bookshop') {
      var deduped = [];
      for (var d = 0; d < next.length; d += 1) {
        var r = next[d];
        if (r.kind === 'generating' && sameKid(r, record)) continue;
        // Symmetric leadId guard (mirrors the generating-save suppression
        // below at ~line 1134): the in-flight generating card for the SAME
        // session is stale the moment that session reaches the bookshop —
        // even when the funnel's photoUrl (/api/signed-image-url) and the
        // bookshop's (/api/preview-leads) resolve to different storage
        // PATHS, so sameKid()'s pathname compare misses and leaves both
        // cards (Lindsey QA 2026-06-27: "I uploaded Max once … 2 separate
        // status mini-modals"). leadId is per-session, so twins (distinct
        // leadIds) and genuine fresh runs (new leadId) are left untouched.
        if (
          r.kind === 'generating' &&
          record.leadId &&
          r.leadId === record.leadId
        ) {
          continue;
        }
        deduped.push(r);
      }
      next = deduped;
    }

    // Generating dedupe: one resume row per kid. Re-open → close can
    // write a second leadId while the first is still in storage (loader)
    // even though polling already flipped the first to done (ready).
    // Lindsey QA 2026-05-19 — duplicate loader + ready cards.
    if (record.kind === 'generating') {
      // Same-session back-nav suppression (Lindsey QA 2026-05-20):
      // post-PR-654, BookDetail in express mode renders a back button
      // that uses window.history.back() to return to the funnel's
      // Reveal screen. The customer's flow:
      //   reach bookshop → close → bookshop record saved
      //   reopen / back-nav → land on Reveal → close → generating
      //                       record gets saved
      // Both records end up in storage and the storefront paints
      // BOTH cards ("Sweetheart's Collection" + "Meet Sweetheart's
      // character"). The bookshop card is the higher-state progress
      // marker — the customer already reached checkout-ready state —
      // so the trailing generating save is noise.
      //
      // We use leadId as the cross-kind identity signal (not sameKid)
      // because leadId is per-session: a TRULY fresh funnel run for
      // the same kid mints a new lead row with a new leadId, so the
      // suppress check correctly does NOT fire and both records stay
      // (preserving the "fresh run" semantics that the bookshop dedup
      // above is forward-only for).
      //
      // Runs BEFORE the same-kid dedup below so we exit early without
      // touching the existing storage state.
      //
      // Ordering note: this block runs AFTER `next.unshift(record)` at
      // line 1017. The early `return` is safe today because no `writeAll`
      // has fired yet — the unshifted-but-not-persisted array is just
      // a local var that goes out of scope. If a future refactor moves
      // `writeAll` earlier or splits this function, this guard must
      // also move before the unshift; otherwise an orphan write could
      // commit the suppressed record. The `isRedundantInFlightGenerating`
      // render-time filter at line 1266 IS supposed to catch the same
      // case downstream, but it uses `sameKid()` which strict-equals
      // signed photoUrls that differ by signing timestamp (funnel signs
      // via /api/signed-image-url, bookshop signs via /api/preview-leads
      // — different requests, different `?token=&exp=` query strings,
      // same underlying object). So render-time filtering is partially
      // dead for this scenario, and save-time suppression here is the
      // only path that actually catches it.
      if (record.leadId) {
        for (var bi = 0; bi < next.length; bi += 1) {
          var bookshopRow = next[bi];
          if (
            bookshopRow.kind === 'bookshop' &&
            bookshopRow.leadId === record.leadId
          ) {
            return; // suppress — bookshop card already represents this session
          }
        }
      }
      // Kid already has a ready card — ignore a stale in-flight close.
      // Run BEFORE we drop other sameKid rows so we don't erase the done
      // record we're comparing against.
      if (record.pipelineStatus !== 'done') {
        for (var ri = 0; ri < next.length; ri += 1) {
          var ex = next[ri];
          if (ex === record) continue;
          if (!sameKid(ex, record)) continue;
          // Only suppress when the kid is already ready on a generating
          // card. A bookshop record may coexist (forward-only fresh run).
          if (ex.kind === 'generating' && ex.pipelineStatus === 'done') return;
        }
      }
      var genDeduped = [];
      for (var gd = 0; gd < next.length; gd += 1) {
        var gr = next[gd];
        if (gr.kind === 'generating' && sameKid(gr, record) && recordKey(gr) !== key) {
          continue;
        }
        genDeduped.push(gr);
      }
      next = genDeduped;
    }

    if (next.length > MAX_KIDS) next.length = MAX_KIDS;
    writeAll(next);
    // Drop the legacy bookshop-only key now that we have unified storage.
    if (typeof localStorage !== 'undefined') {
      try { localStorage.removeItem(LEGACY_BOOKSHOP_KEY); } catch (e) { /* ignore */ }
    }
  }

  /** Backward-compatible single-record reader. Returns the newest kid, or
   *  null if there are none. New code that needs all kids should use
   *  loadAll() directly. */
  function loadSaved() {
    var all = loadAll();
    return all.length > 0 ? all[0] : null;
  }

  /** Drop a specific kid by its key (or all if no key given). Used by the
   *  per-card dismiss button (with a key) and by the "clear everything"
   *  paths (without a key). */
  function clearSaved(key) {
    if (typeof localStorage === 'undefined') return;
    if (!key) {
      try { localStorage.removeItem(RESUME_KEY); } catch (e) { /* ignore */ }
      return;
    }
    var current = loadAll();
    var next = [];
    for (var i = 0; i < current.length; i += 1) {
      if (recordKey(current[i]) !== key) next.push(current[i]);
    }
    writeAll(next);
  }

  /** Patch a kid in-place, keyed by recordKey. If no key is supplied, falls
   *  back to patching the newest kid (preserves v1 polling behavior). */
  function patchSaved(patch, key) {
    var current = loadAll();
    if (current.length === 0) return null;
    var targetKey = key || recordKey(current[0]);
    var updated = null;
    var next = [];
    for (var i = 0; i < current.length; i += 1) {
      if (recordKey(current[i]) === targetKey) {
        var merged = current[i];
        for (var k in patch) {
          if (Object.prototype.hasOwnProperty.call(patch, k)) merged[k] = patch[k];
        }
        updated = merged;
        next.push(merged);
      } else {
        next.push(current[i]);
      }
    }
    writeAll(next);
    return updated;
  }

  /* ─── Polling + ticker (generating variant only) ─── */

  var pollTimerId = null;
  var tickerTimerId = null;
  // Repaint the time-calibrated bar + % + elapsed counter once a second.
  // (Was an 8s slide crawl; the bar is now driven by real elapsed time so
  // it needs a sub-second-ish cadence to read smoothly and tick the clock.)
  var TICK_MS = 1000;

  function stopPolling() {
    if (pollTimerId !== null) { clearTimeout(pollTimerId); pollTimerId = null; }
  }
  function stopTicker() {
    if (tickerTimerId !== null) { clearInterval(tickerTimerId); tickerTimerId = null; }
  }

  // Steady 1s repaint. Self-terminating: renderResumeStack() calls
  // stopTicker() whenever nothing is in-flight, and a kid that reaches
  // 'done'/'failed' is no longer in-flight, so the interval is cleared
  // once the real terminal signal lands — the hold-at-ceiling can never
  // wedge permanently (cf. the poster "stuck at 92%" incident).
  function startTicker() {
    stopTicker();
    tickerTimerId = setInterval(repaintProgressBar, TICK_MS);
  }

  // Server-side pipeline kickoff, tracked per-lead. The endpoint is
  // idempotent (5-min reservation window) but we still keep an in-memory
  // backoff so we don't re-POST every polling tick for the same lead.
  var SERVER_KICKOFF_BACKOFF_MS = 60 * 1000; // don't re-POST more than once a minute per lead
  var lastServerKickoffAtByLead = {}; // leadId → timestamp

  function ensureServerPipelineRunning(leadId, editToken) {
    var prev = lastServerKickoffAtByLead[leadId] || 0;
    if (Date.now() - prev < SERVER_KICKOFF_BACKOFF_MS) return;
    lastServerKickoffAtByLead[leadId] = Date.now();
    var funnelOrigin = new URL(FUNNEL_URL).origin;
    // /api/preview/run is lead-scoped auth-gated. Without the editToken
    // this POST will 401 — the call is silent-fail by design (catch swallow)
    // so the customer doesn't see a flash, but the resume card can't make
    // progress either. The poll-tick that called us will detect the same
    // 401 on its next GET and tear down the card via dropOnAuthFail().
    var headers = { 'content-type': 'application/json' };
    if (editToken) headers['X-Scribbly-Edit-Token'] = editToken;
    fetch(funnelOrigin + '/api/preview/run', {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: headers,
      body: JSON.stringify({ leadId: leadId }),
    }).catch(function () {
      // Network blip — next poll will retry. Don't surface to the customer;
      // the worst case is the server pipeline kicks off on the next tick.
    });
  }

  /** Predicate: should we still be polling this kid? Generating, not done,
   *  inside the hard cap. */
  function isInFlight(record) {
    if (!record || record.kind !== 'generating') return false;
    if (record.pipelineStatus === 'done') return false;
    if (Date.now() - record.savedAt > POLL_HARD_CAP_MS) return false;
    return true;
  }

  /** Drop stale in-flight generating rows for the same kid when another
   *  record already shows them ready (done generating or bookshop). */
  function pruneStaleGeneratingDuplicates(keepKey) {
    var anchor = findRecord(keepKey);
    if (!anchor) return;
    var all = loadAll();
    var kept = [];
    var changed = false;
    for (var i = 0; i < all.length; i += 1) {
      var r = all[i];
      if (
        r.kind === 'generating' &&
        sameKid(r, anchor) &&
        recordKey(r) !== keepKey &&
        r.pipelineStatus !== 'done'
      ) {
        changed = true;
        continue;
      }
      kept.push(r);
    }
    if (changed) writeAll(kept);
  }

  /** True when this generating row is redundant — the SAME SESSION already
   *  has a higher-state card in storage, so showing both would double up one
   *  session (resume-card UX spec rule #1: one card per session, bookshop
   *  face supersedes generating face). Used at render time as a belt-and-
   *  suspenders guard. Per-session (leadId) → genuine twins / fresh re-runs
   *  (distinct leadIds) keep their own cards, which is correct. */
  function isRedundantInFlightGenerating(rec, all) {
    if (!rec || rec.kind !== 'generating') return false;
    var done = rec.pipelineStatus === 'done';
    for (var i = 0; i < all.length; i += 1) {
      var other = all[i];
      if (other === rec) continue;
      // Same-session bookshop record (matching leadId) always supersedes this
      // generating card — whether it's still in-flight OR has just flipped to
      // done. This is checked BEFORE the sameKid gate so it heals dupes that
      // predate the save-time guard, AND (the done case) prevents a generating
      // card that resolves to done on a return visit from rendering a second
      // "Meet <name>'s character" card alongside the session's Collection.
      // Matched on leadId so twins / fresh runs (distinct leadIds) are kept.
      if (other.kind === 'bookshop' && rec.leadId && other.leadId === rec.leadId) {
        return true;
      }
      // The remaining fuzzy (sameKid) checks apply only to a still-in-flight
      // card. A DONE generating card with no same-session bookshop is a
      // legitimate "Meet <name>'s character" card and must keep showing.
      if (done) continue;
      if (!sameKid(other, rec)) continue;
      if (other.kind === 'bookshop') return true;
      if (other.kind === 'generating' && other.pipelineStatus === 'done') {
        return true;
      }
    }
    return false;
  }

  /** Poll a single kid's lead row. On a successful response, patch the
   *  record (status / photoUrl backfill / readyAt) and re-render the
   *  stack. The single `pollTimerId` schedules pollAllInFlight(); each
   *  tick fans out to pollOne() for every in-flight kid in storage. */
  function pollOne(record) {
    var key = recordKey(record);
    var leadId = record.leadId;
    var editToken = record.editToken || null;
    var funnelOrigin = new URL(FUNNEL_URL).origin;
    // Include the lead-scoped edit token if we have it. Without it the
    // GET 401s (server-side strict-deny on missing token), and we treat
    // the 401 the same as a 404 — the card is no longer pollable on
    // this device, so clear it rather than spinning forever.
    var headers = {};
    if (editToken) headers['X-Scribbly-Edit-Token'] = editToken;
    return fetch(funnelOrigin + '/api/preview-leads?id=' + encodeURIComponent(leadId), {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      headers: headers,
    }).then(function (r) {
      // 404 — the lead row was deleted; the card is truly dead, detach.
      // 401 is NOT a delete signal: a deploy blip, clock-skewed token
      // check, or transient auth-middleware failure must not vanish a
      // customer's mini-modal mid-generation (the same posture
      // clearPurchasedResumeCards already takes). Keep the card and
      // retry on the next tick.
      if (r.status === 404) { clearSaved(key); renderResumeStack(); return null; }
      if (!r.ok) return null; // 401 / 5xx / network — silent retry on next tick
      return r.json();
    }).then(function (body) {
      if (!body) return;
      var nextStatus = typeof body.pipeline_status === 'string' ? body.pipeline_status : 'idle';
      // illustration_url being non-null is the canonical "ready" signal.
      // pipeline_status may lag the URL write by a tick depending on the
      // funnel's update order, so trust either signal independently.
      if (body.illustration_url) nextStatus = 'done';
      var fresh = findRecord(key);
      if (!fresh || fresh.kind !== 'generating') return;
      // Backfill OR refresh photoUrl from the lead row.
      //
      // Backfill: when the close payload didn't have a photoUrl yet
      // because the upload was still in flight at close time. The
      // first poll after upload completion paints the photo on the
      // resume card per the Figma spec.
      //
      // Refresh: when the customer did a "Not quite..." photo replace
      // mid-flow. The new upload PATCHes lead.photo_url, but the
      // stored card still has the OLD photoUrl from the initial
      // close — so without this refresh the resume card keeps
      // showing the old photo until manually dismissed. QA-flagged
      // 2026-05-12.
      //
      // Compare path-without-query so we don't churn on signed-URL
      // rotation. The server returns a fresh signed URL on every poll
      // (different ?token=...&exp=... each time) but the underlying
      // storage path only changes when the customer actually swaps
      // photos. Comparing the URL with the query stripped catches the
      // real photo-change case and ignores the rotation.
      if (
        photoUrlOK(body.photo_url) &&
        photoPathChanged(fresh.photoUrl, body.photo_url)
      ) {
        patchSaved({ photoUrl: body.photo_url }, key);
        renderResumeStack();
      }
      var startedAtStr = typeof body.pipeline_started_at === 'string'
        ? body.pipeline_started_at
        : null;
      var startedAtMs = startedAtStr ? Date.parse(startedAtStr) : NaN;
      // Server-side completion safety net. If the lead is still in-flight
      // but the server-side runner hasn't been kicked yet (or its kickoff
      // is stale), POST /api/preview/run to take over from the
      // client pipeline that died when the customer closed the iframe.
      // Never kick the server for a profile-incomplete lead: the run
      // endpoint rejects rows without a pronoun, so the old behavior
      // POSTed a doomed kickoff up to once a minute forever.
      var profileIncomplete = fresh.pipelineStatus === 'profile_incomplete'
        && (nextStatus === 'idle' || !body.pipeline_started_at);
      if (nextStatus !== 'done' && nextStatus !== 'failed' && !profileIncomplete) {
        var stale = isNaN(startedAtMs) || (Date.now() - startedAtMs > 30 * 1000);
        if (stale) ensureServerPipelineRunning(leadId, editToken);
      }
      // Persist the authoritative server start so the time-calibrated bar
      // and the elapsed counter stay correct across close → reopen. This
      // doesn't change the card's structure, so no renderResumeStack() —
      // the 1s repaint ticker picks it up from loadAll() on its next tick.
      if (startedAtStr && !isNaN(startedAtMs) && fresh.pipelineStartedAt !== startedAtStr) {
        patchSaved({ pipelineStartedAt: startedAtStr }, key);
      }
      // Don't let the server's 'idle' (it has no profile_incomplete
      // notion) downgrade the card back into the fake-generating state —
      // hold 'profile_incomplete' until real progress shows up.
      if (profileIncomplete) return;
      if (fresh.pipelineStatus !== nextStatus) {
        var ready = nextStatus === 'done' && !fresh.readyAt;
        var patch = { pipelineStatus: nextStatus };
        if (ready) patch.readyAt = Date.now();
        patchSaved(patch, key);
        if (nextStatus === 'done') pruneStaleGeneratingDuplicates(key);
        renderResumeStack();
      }
    }).catch(function () { /* network blip — silent retry on next tick */ });
  }

  /** Look up a single record from storage by its key. Returns null if no
   *  matching kid is still in storage (e.g. dismissed mid-poll). */
  function findRecord(key) {
    var all = loadAll();
    for (var i = 0; i < all.length; i += 1) {
      if (recordKey(all[i]) === key) return all[i];
    }
    return null;
  }

  /** Tick: poll every in-flight kid. Schedules the next tick if at least
   *  one kid is still in flight. */
  function pollAllInFlight() {
    var all = loadAll();
    var inFlight = [];
    for (var i = 0; i < all.length; i += 1) {
      if (isInFlight(all[i])) inFlight.push(all[i]);
    }
    if (inFlight.length === 0) { stopPolling(); return; }
    for (var j = 0; j < inFlight.length; j += 1) pollOne(inFlight[j]);
    schedulePoll();
  }

  function schedulePoll() {
    if (pollTimerId !== null) return; // already scheduled
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    pollTimerId = setTimeout(function () {
      pollTimerId = null;
      pollAllInFlight();
    }, POLL_INTERVAL_MS);
  }

  /** Bookshop thumbnail refresh — one-shot per page load.
   *
   *  Bookshop-kind resume records store a `photoUrl` that is a 1h signed
   *  URL from /api/preview-leads (post-bookshop-private-bucket-regressions).
   *  The token in that URL expires after 1h, after which the <img src="…">
   *  on the resume card 403s and the customer sees a broken/placeholder
   *  thumbnail until they dismiss the card. The card itself can live up
   *  to RESUME_TTL_MS (24 hours — Lindsey confirmed 2026-08-24) — far longer than the signed URL — so
   *  the thumbnail and the card outlive each other badly without a
   *  refresh path.
   *
   *  isInFlight() / pollAllInFlight() only target `generating` records,
   *  so they never touch this. We fire a single GET per bookshop-kind
   *  record with leadId + editToken at page-load time, swap in the
   *  fresh signed URL when it differs from what's stored, and let the
   *  next render pick it up. No polling loop — once per page load is
   *  enough since the customer is unlikely to keep a storefront tab
   *  open >1h without a navigation.
   *
   *  Legacy records (saved before the polling loop captured editToken
   *  onto bookshop records) lack the token; they're skipped here.
   *  Those cards degrade gracefully today: the photo column already
   *  swaps to the placeholder on <img> error, and the Open button
   *  still navigates regardless of thumbnail health.
   *
   *  On 401/404: the record is no longer accessible from this device
   *  (token revoked or lead row deleted) — clear it and re-render so
   *  the customer isn't left with a card that can never recover.
   */
  function refreshBookshopThumbnails() {
    var all = loadAll();
    var funnelOrigin = new URL(FUNNEL_URL).origin;
    for (var i = 0; i < all.length; i += 1) {
      var rec = all[i];
      if (rec.kind !== 'bookshop') continue;
      if (!rec.leadId || !rec.editToken) continue; // legacy record — skip
      var key = recordKey(rec);
      var leadId = rec.leadId;
      var editToken = rec.editToken;
      // Closure capture for the .then() callbacks below — `rec` would
      // otherwise be reassigned on the next iteration before the fetch
      // resolves and we'd compare against the wrong photoUrl.
      (function (recSnapshot, recKey) {
        fetch(funnelOrigin + '/api/preview-leads?id=' + encodeURIComponent(leadId), {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          headers: { 'X-Scribbly-Edit-Token': editToken },
        }).then(function (r) {
          // 404 mirrors pollOne — the row is gone, drop the record. 401
          // does NOT clear (transient auth blips must not vanish a
          // card); the thumbnail just stays as-is until a later pass.
          if (r.status === 404) {
            clearSaved(recKey);
            renderResumeStack();
            return null;
          }
          if (!r.ok) return null; // 5xx / blip — leave the existing URL, try again next page load
          return r.json();
        }).then(function (body) {
          if (!body) return;
          if (!photoUrlOK(body.photo_url)) return;
          // photoPathChanged: only patch when the path component
          // (everything before the query string) actually changed.
          // Comparing the full URLs would force a patch every refresh
          // because the signed-URL query string includes a fresh token
          // and expiry each call — and patching triggers a card
          // rebuild via renderResumeStack(). We only want to repaint
          // when the underlying object path moved.
          if (!photoPathChanged(recSnapshot.photoUrl, body.photo_url)) return;
          patchSaved({ photoUrl: body.photo_url }, recKey);
          renderResumeStack();
        }).catch(function () {
          // Network blip — leave the stored URL in place; next page
          // load will retry. Don't surface to the customer.
        });
      })(rec, key);
    }
  }

  /** On return to the storefront, refresh the resume cards that the live
   *  poller skips. startPolling() only polls in-flight kids (generating,
   *  not done, < POLL_HARD_CAP_MS old) and refreshBookshopThumbnails() only
   *  refreshes bookshop kids — so a GENERATING card that's either already
   *  finished or older than the poll cap (e.g. opened the next morning) gets
   *  nothing: its bar stays frozen on the last-seen status and its 1-hour
   *  signed thumbnail has expired (broken image), until the customer taps it.
   *  This does one read per such card via pollOne(): re-signs the thumbnail,
   *  refreshes the status (flips to finished if it completed in the
   *  background), and resumes a genuinely-incomplete run — the same rehydrate
   *  opening the card would do, but on return instead of on tap. */
  /** A resume card needs a one-time rehydrate on return iff it's a generating
   *  kid the live poller won't cover — i.e. NOT in-flight (already finished,
   *  or older than POLL_HARD_CAP_MS). Bookshop kids are refreshed by
   *  refreshBookshopThumbnails(); in-flight generating kids by startPolling(). */
  function shouldRehydrateOnReturn(rec) {
    return !!rec && rec.kind === 'generating' && !isInFlight(rec);
  }

  function rehydrateStaleGenerating() {
    var all = loadAll();
    for (var i = 0; i < all.length; i += 1) {
      if (shouldRehydrateOnReturn(all[i])) pollOne(all[i]);
    }
  }

  /** Compare two photo URLs and decide if the underlying object path
   *  changed (vs. just a refreshed signed-URL token/expiry).
   *
   *  Treats missing-becomes-present and present-becomes-missing as a
   *  change. When both are present, normalizes to the pathname so two
   *  signed URLs that differ only in their query string compare equal.
   *  Falls back to a full-string compare on any URL-parse failure (the
   *  conservative choice — if we can't parse, we'd rather patch and
   *  rebuild than silently miss a real change).
   */
  function photoPathChanged(prev, next) {
    if (prev === next) return false;
    if (!prev || !next) return true;
    try {
      var prevPath = new URL(prev).pathname;
      var nextPath = new URL(next).pathname;
      return prevPath !== nextPath;
    } catch (e) {
      return prev !== next;
    }
  }

  function startPolling() {
    var all = loadAll();
    var anyInFlight = false;
    for (var i = 0; i < all.length; i += 1) {
      if (isInFlight(all[i])) { anyInFlight = true; break; }
    }
    if (!anyInFlight) return;
    // Fire one immediate poll cycle so a returning visitor gets fresh state
    // right away rather than waiting POLL_INTERVAL_MS for the first tick.
    pollAllInFlight();
    startTicker();
  }

  // Re-resolve every card to its true state on each "visit" — and a "visit"
  // is BOTH a fresh page load (initialPaint) AND returning to an
  // already-open tab (no reload). rehydrateStaleGenerating() re-checks the
  // generating cards the live poller has given up on (past the 10-min cap),
  // so a card that finished / failed / stalled while the tab was backgrounded
  // resolves on return instead of sitting frozen until a hard refresh.
  // startPolling() resumes the live, moving bar for genuinely in-flight cards.
  // (Founder UX spec 2026-06-29: "every visit, even without refresh, resolve
  // to its truthy state." The child photo re-signs itself lazily on <img>
  // error — see appendPhotoColumn.)
  function resolveOnVisit() {
    // Paint first. rehydrateStaleGenerating() only re-renders when a
    // record's variant changed, and startPolling() never renders at all —
    // so on a return where the stack was never built (or was built before
    // the record existed) neither of them puts a card on screen. Painting
    // unconditionally is cheap: renderResumeStack() dirty-checks each card
    // and rebuilds only what changed.
    renderResumeStack();
    rehydrateStaleGenerating();
    startPolling();
  }
  // Pause polling when the tab is hidden (honest network footprint); resolve
  // on every return-to-visible.
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        stopPolling();
      } else {
        resolveOnVisit();
      }
    });
  }
  // Some browsers fire `focus` without a visibilitychange (returning from
  // another window rather than another tab) — cover that path too.
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', resolveOnVisit);
  }

  // Cross-tab sync: when another storefront tab writes to localStorage
  // (closes a different kid's funnel, dismisses one of its cards, the
  // poller updates a status), this tab re-renders so its stack reflects
  // the unified state. The `storage` event only fires in OTHER tabs of
  // the same origin, so this won't loop with our own writes.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', function (e) {
      if (e.key === RESUME_KEY || e.key === null) {
        renderResumeStack();
      }
    });
  }

  /* ─── Card render — multi-kid stack ─── */

  // The stack is the wrapper (one DOM element on the page). Each kid in
  // localStorage gets its own .scribbly-resume-card inside the stack. The
  // wrapper handles position (bottom-right corner on desktop, anchor pill
  // on mobile); cards inside are stacked column-reverse so the newest kid
  // is visually highest.
  var resumeStackEl = null;
  // recordKey → {el, lastVariant, lastPhotoUrl, lastStatus} for dirty-checking
  // so we don't rebuild a card's DOM on every render unless something changed.
  var cardEls = {};

  // Paint the bar fill + "NN% · M:SS" meta line for one generating card,
  // straight from real elapsed time. Shared by the 1s ticker and the
  // initial render so they can't drift apart.
  function paintGeneratingProgress(rootEl, rec) {
    if (!rootEl) return;
    var fill = rootEl.querySelector('.scribbly-resume-card__bar-fill');
    var meta = rootEl.querySelector('.scribbly-resume-card__meta');
    var phase = rec.pipelineStatus || 'p1_running';
    var elapsed = recordElapsedMs(rec);
    var pct = pipelineProgressTimed(phase, elapsed, recordDoneElapsedMs(rec));
    if (fill) fill.style.width = (pct * 100) + '%';
    if (meta) meta.textContent = Math.round(pct * 100) + '% · ' + formatElapsed(elapsed);
  }

  function repaintProgressBar() {
    if (!resumeStackEl) return;
    var all = loadAll();
    var needsRerender = false;
    for (var i = 0; i < all.length; i += 1) {
      var rec = all[i];
      if (rec.kind !== 'generating' || rec.pipelineStatus === 'done') continue;
      var entry = cardEls[recordKey(rec)];
      if (!entry || !entry.el) continue;
      // A card that has just crossed into 'standby' (or 'failed') must stop
      // being repainted as a live bar and swap to the message card. Flag a
      // one-time re-render on the FIRST tick after the crossing (guard on the
      // still-'generating' lastVariant so we don't renderResumeStack() every
      // tick for the rest of its standby life).
      if (resumeCardVariant(rec) !== 'generating') {
        if (entry.lastVariant === 'generating') needsRerender = true;
        continue;
      }
      paintGeneratingProgress(entry.el, rec);
    }
    if (needsRerender) renderResumeStack();
  }

  /** Per-kid sessionStorage flags — slide-in animation fires once per session
   *  per kid; just-ready pulse fires once per session per kid. */
  function alreadyShown(key) {
    try { return sessionStorage.getItem('scribbly:resume-shown:' + key) === '1'; }
    catch (e) { return false; }
  }
  function markShown(key) {
    try { sessionStorage.setItem('scribbly:resume-shown:' + key, '1'); } catch (e) { /* ignore */ }
  }
  function alreadyCelebrated(key) {
    try { return sessionStorage.getItem('scribbly:resume-celebrated:' + key) === '1'; }
    catch (e) { return false; }
  }
  function markCelebrated(key) {
    try { sessionStorage.setItem('scribbly:resume-celebrated:' + key, '1'); } catch (e) { /* ignore */ }
  }

  /** Top-level render entry point. Reads loadAll(), reconciles cards in the
   *  DOM (creating new ones, removing kids that aged out / were dismissed),
   *  and starts/stops polling based on whether anything is still in-flight. */
  function renderResumeStack() {
    var stored = loadAll();
    var all = [];
    for (var f = 0; f < stored.length; f += 1) {
      if (!isRedundantInFlightGenerating(stored[f], stored)) all.push(stored[f]);
    }
    if (all.length === 0) {
      if (resumeStackEl && resumeStackEl.parentNode) {
        resumeStackEl.parentNode.removeChild(resumeStackEl);
      }
      resumeStackEl = null;
      cardEls = {};
      stopPolling();
      stopTicker();
      return;
    }

    // Re-attach, not just create. Reusing a cached element that is no
    // longer in the document paints every card into a detached node — the
    // stack is silently invisible with no error. That happens whenever
    // something replaces the page's body content under us (a Shopify
    // section re-render, a theme app, our own earlier teardown), and it
    // is unrecoverable for the rest of the page's life. isConnected is the
    // cheap, exact check.
    if (!resumeStackEl || !resumeStackEl.isConnected) {
      resumeStackEl = document.createElement('div');
      resumeStackEl.className = 'scribbly-resume-stack';
      resumeStackEl.setAttribute('role', 'complementary');
      resumeStackEl.setAttribute('aria-label', 'Your previews and bookshops');
      document.body.appendChild(resumeStackEl);
      // The per-card cache points at children of the old, detached stack;
      // drop it so the cards are rebuilt inside the new one.
      cardEls = {};
    }
    // Mobile-overflow chip ("+ N more"). Inserted into the stack on every
    // render so reconciliation handles it like any other child. CSS gates
    // its visibility behind .scribbly-resume-stack--has-overflow + a
    // ≤479px viewport — desktop and small-stack cases never see it.
    var moreChip = resumeStackEl.querySelector('.scribbly-resume-stack__more');
    if (!moreChip) {
      moreChip = document.createElement('button');
      moreChip.type = 'button';
      moreChip.className = 'scribbly-resume-stack__more';
      moreChip.setAttribute('aria-controls', 'scribbly-resume-stack');
      moreChip.addEventListener('click', function () {
        resumeStackEl.classList.toggle('scribbly-resume-stack--expanded');
        var expanded = resumeStackEl.classList.contains('scribbly-resume-stack--expanded');
        moreChip.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        updateMoreChipText();
      });
      // The chip lives at flex-end (visually below all cards in the
      // column-reverse layout). Append last so its DOM order is "first
      // visible after the cards" when not column-reversed.
      resumeStackEl.appendChild(moreChip);
    }

    // Reconcile: build a set of current keys, remove cards whose kid is
    // no longer in storage (dismissed / aged out / detached on 404).
    var keepKeys = {};
    for (var i = 0; i < all.length; i += 1) {
      keepKeys[recordKey(all[i])] = true;
    }
    for (var k in cardEls) {
      if (!Object.prototype.hasOwnProperty.call(cardEls, k)) continue;
      if (!keepKeys[k]) {
        var stale = cardEls[k];
        if (stale && stale.el && stale.el.parentNode) {
          stale.el.parentNode.removeChild(stale.el);
        }
        delete cardEls[k];
      }
    }

    // Render in newest-first order. The stack's CSS uses
    // flex-direction: column-reverse so the DOM order (newest first) maps
    // to visual order (newest at the top of the bottom-right corner).
    var anyInFlight = false;
    var anyPhase = null;
    for (var j = 0; j < all.length; j += 1) {
      var rec = all[j];
      renderOneCard(rec);
      // Position attribute powers the mobile collapsed-state CSS, which
      // hides every card except the first (newest) when the stack is
      // collapsed. Position 0 is always visible.
      var entry = cardEls[recordKey(rec)];
      if (entry && entry.el) entry.el.setAttribute('data-stack-position', String(j));
      if (isInFlight(rec)) {
        anyInFlight = true;
        anyPhase = anyPhase || rec.pipelineStatus;
      }
    }

    // Toggle the overflow modifier so the mobile-pill CSS knows to engage.
    // 2+ cards = overflow; on desktop the modifier has no visual effect.
    if (all.length >= 2) {
      resumeStackEl.classList.add('scribbly-resume-stack--has-overflow');
    } else {
      resumeStackEl.classList.remove('scribbly-resume-stack--has-overflow');
      resumeStackEl.classList.remove('scribbly-resume-stack--expanded');
    }
    updateMoreChipText();

    if (anyInFlight) {
      startPolling();
    } else {
      stopPolling();
      stopTicker();
    }
  }

  /** Sync the "+ N more" text and aria state to current state. Called from
   *  renderResumeStack and the chip's own click handler. */
  function updateMoreChipText() {
    if (!resumeStackEl) return;
    var chip = resumeStackEl.querySelector('.scribbly-resume-stack__more');
    if (!chip) return;
    var all = loadAll();
    var hidden = Math.max(0, all.length - 1);
    var expanded = resumeStackEl.classList.contains('scribbly-resume-stack--expanded');
    if (expanded) {
      chip.textContent = 'Show less';
    } else if (hidden > 0) {
      chip.textContent = '+ ' + hidden + ' more';
    } else {
      chip.textContent = '';
    }
  }

  /** The visual variant for a card, derived FRESH on every render so a card
   *  resolves to its true state on every visit (founder UX spec 2026-06-29):
   *   - 'collection' : a bookshop record, OR a generating record that's done
   *   - 'failed'     : generation errored → "something went wrong, tap to retry"
   *   - 'standby'    : still generating but well past the ~75s norm → the calm
   *                    "Stand by" message instead of a bar held at 92%
   *   - 'generating' : actively illustrating, live bar */
  function resumeCardVariant(rec) {
    if (rec.kind !== 'generating') return 'collection';
    if (rec.pipelineStatus === 'done') return 'collection';
    if (rec.pipelineStatus === 'failed') return 'failed';
    // Closed before finishing name/pronoun — nothing is generating, so
    // neither a fake "Illustrating…" bar nor the standby flip is honest.
    // (2026-08-24 close/reopen audit: this state used to render the
    // generating variant, then "Stand by" after 3 minutes, while the
    // poller hammered a kickoff the server rejects.)
    if (rec.pipelineStatus === 'profile_incomplete') return 'incomplete';
    if (recordElapsedMs(rec) > STANDBY_AFTER_MS) return 'standby';
    return 'generating';
  }

  /** Render or update a single card for one kid. Idempotent — calling it
   *  with the same record state is a near-no-op (just updates the progress
   *  fill). When the card's variant changes (generating → collection on
   *  ready, etc.), the inner DOM is rebuilt to swap the right-column UI. */
  function renderOneCard(rec) {
    var key = recordKey(rec);
    var variant = resumeCardVariant(rec);
    var entry = cardEls[key];

    if (!entry) {
      var el = document.createElement('div');
      el.setAttribute('data-kid', key);
      el.setAttribute('role', 'group');
      resumeStackEl.appendChild(el);
      entry = { el: el, lastVariant: null, lastPhotoUrl: null, lastStatus: null };
      cardEls[key] = entry;
    }

    var classes = 'scribbly-resume-card scribbly-resume-card--' + variant;
    if (!alreadyShown(key)) classes += ' scribbly-resume-card--enter';
    if (variant === 'collection' && rec.kind === 'generating'
        && rec.pipelineStatus === 'done' && !alreadyCelebrated(key)) {
      classes += ' scribbly-resume-card--just-ready';
      markCelebrated(key);
    }
    entry.el.className = classes;
    markShown(key);
    var ariaLabel;
    if (variant === 'generating') ariaLabel = 'Illustrating ' + rec.childName;
    else if (variant === 'incomplete') ariaLabel = 'Finish ' + namePossessive(rec.childName) + ' preview';
    else if (variant === 'standby') ariaLabel = 'Still finishing ' + rec.childName + "'s book";
    else if (variant === 'failed') ariaLabel = rec.childName + "'s book had a problem — tap to try again";
    else ariaLabel = rec.childName + "'s collection is ready";
    entry.el.setAttribute('aria-label', ariaLabel);

    // Cheap dirty-check: only rebuild inner DOM when the things that drive
    // child structure (variant / photo / status) have changed. The progress
    // bar fill is updated in-place via repaintProgressBar() on each ticker
    // tick, so we don't need a full rebuild for slide-driven advancement.
    var photoUrl = photoUrlOK(rec.photoUrl) ? rec.photoUrl : null;
    if (
      entry.lastVariant === variant &&
      entry.lastPhotoUrl === photoUrl &&
      entry.lastStatus === rec.pipelineStatus
    ) {
      return;
    }
    entry.lastVariant = variant;
    entry.lastPhotoUrl = photoUrl;
    entry.lastStatus = rec.pipelineStatus || null;

    entry.el.innerHTML = '';
    appendPhotoColumn(entry.el, rec);
    var rightCol = document.createElement('div');
    rightCol.className = 'scribbly-resume-card__right';
    if (variant === 'generating') appendGeneratingRight(rightCol, rec);
    else if (variant === 'incomplete') appendIncompleteRight(rightCol, rec);
    else if (variant === 'standby') appendStandbyRight(rightCol, rec);
    else if (variant === 'failed') appendFailedRight(rightCol, rec);
    else appendCollectionRight(rightCol, rec);
    entry.el.appendChild(rightCol);
    entry.el.appendChild(buildDismissButton(key));
  }

  /** Backward-compatible alias for the public test API and DOMContentLoaded
   *  handler. The script's own internal calls use renderResumeStack(). */
  function renderResumeCard() { renderResumeStack(); }

  /** Resume-card cap toast. Shown when the customer clicks a funnel CTA
   *  but already has MAX_KIDS in-flight resume cards. Styling mirrors
   *  the resume cards (white pill, soft shadow, serif body) so it feels
   *  like part of the same family. Auto-dismisses after a short read
   *  window or on explicit click; only one toast can exist at a time. */
  var capToastEl = null;
  var capToastTimerId = null;
  var CAP_TOAST_TIMEOUT_MS = 6000;

  function showCapToast() {
    // Idempotent: if a toast is already up, restart its timer rather than
    // stacking duplicates.
    if (capToastEl) {
      clearTimeout(capToastTimerId);
      capToastTimerId = setTimeout(hideCapToast, CAP_TOAST_TIMEOUT_MS);
      return;
    }
    capToastEl = document.createElement('div');
    capToastEl.className = 'scribbly-cap-toast';
    capToastEl.setAttribute('role', 'status');
    capToastEl.setAttribute('aria-live', 'polite');

    var body = document.createElement('div');
    body.className = 'scribbly-cap-toast__body';
    // ASCII-escape '’' instead of the literal curly apostrophe so
    // the source survives Shopify Admin's UTF-8 → Latin-1 paste mangling.
    // See PR #?? for the previous encoding incident.
    body.textContent =
      'You\u2019ve got ' + MAX_KIDS + ' book previews in progress already. '
      + 'Finish or dismiss one to start another.';

    var dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'scribbly-cap-toast__dismiss';
    dismiss.setAttribute('aria-label', 'Dismiss notice');
    dismiss.textContent = '\u00D7';
    dismiss.addEventListener('click', hideCapToast);

    capToastEl.appendChild(body);
    capToastEl.appendChild(dismiss);
    document.body.appendChild(capToastEl);

    capToastTimerId = setTimeout(hideCapToast, CAP_TOAST_TIMEOUT_MS);
  }

  function hideCapToast() {
    if (capToastTimerId !== null) {
      clearTimeout(capToastTimerId);
      capToastTimerId = null;
    }
    if (capToastEl && capToastEl.parentNode) {
      capToastEl.parentNode.removeChild(capToastEl);
    }
    capToastEl = null;
  }

  function collapsePhotoToPlaceholder(photoCol, img) {
    photoCol.classList.add('scribbly-resume-card__photo--placeholder');
    if (img && img.parentNode) img.parentNode.removeChild(img);
  }

  function appendPhotoColumn(parent, saved) {
    var photoCol = document.createElement('div');
    photoCol.className = 'scribbly-resume-card__photo';

    var canResign = !!(saved.leadId && saved.editToken);
    var hasStored = photoUrlOK(saved.photoUrl);

    // Nothing to show and no way to fetch it (cross-device / no token) →
    // the soft placeholder frame, as before.
    if (!hasStored && !canResign) {
      photoCol.classList.add('scribbly-resume-card__photo--placeholder');
      parent.appendChild(photoCol);
      return;
    }

    var img = document.createElement('img');
    img.alt = saved.childName + "'s photo";
    // referrerpolicy=no-referrer so the Supabase storage logs don't tie the
    // request to the merchant's storefront URL — a small privacy nicety.
    img.setAttribute('referrerpolicy', 'no-referrer');
    var loaded = false;
    var resignDone = false;
    img.addEventListener('load', function () { loaded = true; });
    img.addEventListener('error', function () {
      // The current src failed. If a proactive re-sign is still in flight it
      // will swap a fresh URL in — wait for it. Otherwise (no token, or the
      // re-sign already ran) collapse to the placeholder.
      if (!canResign || resignDone) collapsePhotoToPlaceholder(photoCol, img);
    });
    if (hasStored) {
      img.src = saved.photoUrl;
    } else {
      // No stored URL yet — show the placeholder frame until the proactive
      // re-sign below fills it in.
      photoCol.classList.add('scribbly-resume-card__photo--placeholder');
    }
    photoCol.appendChild(img);

    // PROACTIVE re-sign on arrival. The stored photoUrl is a ~1h signed link
    // but a card lives up to 24h, so on a return it's frequently expired.
    // Rather than render it and WAIT for the <img> to fail, we fetch a fresh
    // signed URL from the lead row right away, in parallel. We swap it in
    // UNLESS the stored URL already loaded fine (so a still-valid photo never
    // needlessly reloads). Display-only — we don't persist it, so renders
    // never churn storage. (Founder requirement 2026-06-29: the child's photo
    // is ALWAYS in the thumbnail, instantly, on every return — spec rule #8.)
    // The <img> error handler above is the final backstop.
    if (canResign) {
      var funnelOrigin = new URL(FUNNEL_URL).origin;
      fetch(funnelOrigin + '/api/preview-leads?id=' + encodeURIComponent(saved.leadId), {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'X-Scribbly-Edit-Token': saved.editToken },
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (body) {
          resignDone = true;
          if (loaded) return; // stored URL was still valid — keep it, no reload
          if (body && photoUrlOK(body.photo_url)) {
            photoCol.classList.remove('scribbly-resume-card__photo--placeholder');
            img.src = body.photo_url;
          } else if (!hasStored) {
            collapsePhotoToPlaceholder(photoCol, img);
          }
          // else: leave the stored URL up — it hasn't failed, and its own
          // error handler will placeholder it if it does.
        })
        .catch(function () {
          resignDone = true;
          if (!loaded && !hasStored) collapsePhotoToPlaceholder(photoCol, img);
        });
    }

    parent.appendChild(photoCol);
  }

  function appendGeneratingRight(parent, saved) {
    var heading = document.createElement('div');
    heading.className = 'scribbly-resume-card__heading';
    // Single text node — emoji is inline in the textContent (no extra span)
    // so the layout matches the Figma's left-aligned italic title row.
    heading.textContent = '🎨 Illustrating ' + saved.childName + '…';

    var bar = document.createElement('div');
    bar.className = 'scribbly-resume-card__bar';
    var fill = document.createElement('div');
    fill.className = 'scribbly-resume-card__bar-fill';
    bar.appendChild(fill);

    // "NN% · M:SS" readout under the bar. Filled (and kept live) by
    // paintGeneratingProgress — here and on every 1s ticker repaint.
    var meta = document.createElement('div');
    meta.className = 'scribbly-resume-card__meta';

    var cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'scribbly-resume-card__cta';
    cta.textContent = 'Open';
    cta.addEventListener('click', function () {
      open({
        resumeLeadId: saved.leadId,
        occasion: saved.occasion,
        express: saved.express,
        bookId: saved.bookId,
      });
    });

    parent.appendChild(heading);
    parent.appendChild(bar);
    parent.appendChild(meta);
    parent.appendChild(cta);
    // Initial paint so the bar + readout are correct on first render,
    // not blank until the first ticker tick a second later.
    paintGeneratingProgress(parent, saved);
  }

  // Re-enter the funnel for this lead (Reveal/Waiting screen). Shared by the
  // generating, standby and failed cards — for `failed` this re-runs the
  // pipeline, which is the "try again" action.
  function openGeneratingResume(saved) {
    open({
      resumeLeadId: saved.leadId,
      occasion: saved.occasion,
      express: saved.express,
      bookId: saved.bookId,
    });
  }

  // "Stand by" — a still-generating book that's run well past the ~75s norm.
  // Universal copy: NO promise of a text/email (contact is optional). No bar
  // (a held-at-92% bar reads as frozen). Founder UX spec 2026-06-29 (B).
  // Profile-incomplete — the customer closed before name/pronoun, so no
  // pipeline exists yet. Plain invitation, no bar, and Open resumes at
  // exactly the unfinished step (resumeRouting handles that already).
  function appendIncompleteRight(parent, saved) {
    var heading = document.createElement('div');
    heading.className = 'scribbly-resume-card__heading';
    heading.textContent = '✏️ Finish ' + namePossessive(saved.childName) + ' preview';
    var cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'scribbly-resume-card__cta';
    cta.textContent = 'Open';
    cta.addEventListener('click', function () { openGeneratingResume(saved); });
    parent.appendChild(heading);
    parent.appendChild(cta);
  }

  function appendStandbyRight(parent, saved) {
    var msg = document.createElement('div');
    msg.className = 'scribbly-resume-card__message';
    msg.textContent = 'We\'re still finishing ' + saved.childName
      + '\'s book — this one\'s taking a little longer than usual. Stand by.';
    var cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'scribbly-resume-card__cta';
    cta.textContent = 'Open';
    cta.addEventListener('click', function () { openGeneratingResume(saved); });
    parent.appendChild(msg);
    parent.appendChild(cta);
  }

  // Failed generation — clear error + tap-to-retry (re-runs the pipeline).
  // Never leaves a fake "Illustrating…" bar. Founder UX spec 2026-06-29 (A).
  function appendFailedRight(parent, saved) {
    var msg = document.createElement('div');
    msg.className = 'scribbly-resume-card__message';
    msg.textContent = 'Something went wrong with ' + saved.childName
      + '\'s book — tap to try again.';
    var cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'scribbly-resume-card__cta';
    cta.textContent = 'Try again';
    cta.addEventListener('click', function () { openGeneratingResume(saved); });
    parent.appendChild(msg);
    parent.appendChild(cta);
  }

  function appendCollectionRight(parent, saved) {
    var heading = document.createElement('div');
    heading.className = 'scribbly-resume-card__collection';
    var line1 = document.createElement('div');
    var line2 = document.createElement('div');
    var emoji = document.createElement('span');
    emoji.setAttribute('aria-hidden', 'true');

    // Two visually distinct copy variants share the same italic two-line
    // heading style. A bookshop record (kind='bookshop') opens the
    // bookshop; a generating record that finished (kind='generating' +
    // status='done') opens the funnel back at the Reveal screen so the
    // customer can meet the character. Both records can coexist for the
    // same kid (different keys → different cards) — so they MUST read
    // distinctly to avoid the "two identical Collection cards" bug.
    if (saved.kind === 'bookshop') {
      // namePossessive handles the AP-style trailing-s rule
      // (Charles → Charles’ rather than Charles’s). QA-flagged.
      line1.textContent = namePossessive(saved.childName);
      emoji.textContent = '📚';
      line2.appendChild(emoji);
      line2.appendChild(document.createTextNode(' Collection'));
    } else {
      line1.textContent = 'Meet ' + namePossessive(saved.childName);
      emoji.textContent = '✨';
      line2.appendChild(emoji);
      line2.appendChild(document.createTextNode(' character'));
    }
    heading.appendChild(line1);
    heading.appendChild(line2);

    var cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'scribbly-resume-card__cta';
    cta.textContent = 'Open';
    cta.addEventListener('click', function () {
      // Bookshop closes have a slug → open the bookshop directly.
      // Generating-completed-now closes have a leadId → open the funnel
      // resumed; the Reveal screen lands and they can navigate onward.
      if (saved.kind === 'bookshop' && saved.slug) {
        open({
          bookshopSlug: saved.slug,
          occasion: saved.occasion,
          express: saved.express,
          bookId: saved.bookId,
          // leadId rides the URL fragment into the bookshop so the iframe
          // can locate its portrait token in localStorage. Absent on
          // pre-rollout records — the bookshop's locked view is the
          // expected fallback there.
          leadId: saved.leadId,
        });
      } else if (saved.leadId) {
        open({
          resumeLeadId: saved.leadId,
          occasion: saved.occasion,
          express: saved.express,
          bookId: saved.bookId,
        });
      }
    });

    parent.appendChild(heading);
    parent.appendChild(cta);
  }

  /** Dismiss a kid by record key, with cascade. Used by the per-card ×.
   *
   *  Cascade: dismissing a `bookshop` card also clears any matching
   *  `generating` record (same kid by sameKid()). Otherwise a kid that
   *  has both records in storage would only have the bookshop one
   *  removed; the hidden generating record would resurface as soon as
   *  it became the only one left. Lindsey's intent on dismiss is "I'm
   *  done with this kid" — so wipe the whole kid. (2026-05-07.)
   *
   *  Reverse direction is intentionally left alone: dismissing a
   *  `generating` card should not erase a bookshop record for the
   *  same kid — the bookshop record is a more permanent record of
   *  progress and shouldn't be wiped by the dismissal of a stale
   *  funnel-resume card. */
  function dismissKid(key) {
    var all = loadAll();
    var target = null;
    for (var i = 0; i < all.length; i += 1) {
      if (recordKey(all[i]) === key) { target = all[i]; break; }
    }
    if (target && target.kind === 'bookshop') {
      for (var j = 0; j < all.length; j += 1) {
        var r = all[j];
        if (r.kind === 'generating' && sameKid(r, target)) {
          clearSaved(recordKey(r));
        }
      }
    }
    clearSaved(key);
  }

  /** Build the per-card × dismiss button. Closes only this kid; other
   *  cards in the stack are unaffected. The polling loop restarts itself
   *  from renderResumeStack() with the remaining in-flight kids. */
  function buildDismissButton(key) {
    var dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'scribbly-resume-card__dismiss';
    dismiss.setAttribute('aria-label', 'Dismiss');
    dismiss.textContent = '\u00D7';
    dismiss.addEventListener('click', function () {
      dismissKid(key);
      renderResumeStack();
    });
    return dismiss;
  }

  /** Post-purchase resume-card clear (one-shot per page load).
   *
   *  Once a kid's book is bought, their floating resume card(s) are stale
   *  — but nothing on the storefront knew about the order, so a card
   *  lingered until the 24-hour TTL or a manual dismiss (Lindsey QA
   *  2026-06-27: "after I purchased … the mini-modal should disappear").
   *
   *  Fire one GET per bookshop-kind record holding leadId + editToken at
   *  /api/lead-purchased (edit-token gated; reuses the Shopify purchase
   *  cache). On an explicit { purchased: true } drop that record AND any
   *  sibling sharing the same leadId — so a leftover in-flight
   *  "Illustrating <name>…" card vanishes alongside "<name>'s Collection"
   *  — then re-render.
   *
   *  CLEAR-ON-TRUE ONLY: a 401 (token blip), 5xx, network error, or
   *  { purchased: false } all leave the card untouched. Purchase is the
   *  sole signal that erases a card here — never an auth/transport failure
   *  — so a flaky check can't wipe a customer's in-progress card. (This
   *  deliberately does NOT mirror refreshBookshopThumbnails' clear-on-401,
   *  which is about token revocation, not purchase.)
   *
   *  We poll BOOKSHOP records, not generating ones: the bookshop record
   *  supersedes the generating one on save, so bookshop-only is the steady
   *  state and is what actually catches the post-purchase case. A
   *  generating-only card can't have been purchased (you can't reach
   *  checkout without the bookshop), so there's nothing to check against.
   */
  function clearPurchasedResumeCards() {
    var all = loadAll();
    var funnelOrigin = new URL(FUNNEL_URL).origin;
    for (var i = 0; i < all.length; i += 1) {
      var rec = all[i];
      if (rec.kind !== 'bookshop') continue;
      if (!rec.leadId || !rec.editToken) continue; // not pollable — keep
      (function (leadId, editToken) {
        fetch(funnelOrigin + '/api/lead-purchased?id=' + encodeURIComponent(leadId), {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          headers: { 'X-Scribbly-Edit-Token': editToken },
        }).then(function (r) {
          if (!r.ok) return null; // 401 / 5xx — keep the card, retry next load
          return r.json();
        }).then(function (body) {
          if (!body || body.purchased !== true) return; // keep the card
          // Purchased — drop every record for this session. The bookshop
          // card + any leftover generating sibling share the leadId;
          // twins (distinct leadIds) and fresh runs are untouched.
          var current = loadAll();
          var next = [];
          for (var k = 0; k < current.length; k += 1) {
            if (current[k].leadId !== leadId) next.push(current[k]);
          }
          if (next.length !== current.length) {
            writeAll(next);
            renderResumeStack();
          }
        }).catch(function () { /* network blip — keep the card */ });
      })(rec.leadId, rec.editToken);
    }
  }

  // Paint on first page-load. Safe whether the DOM is parsed yet or not —
  // we append to body, which exists by the time the script runs (script
  // is loaded at the bottom of the theme.liquid per shopify/README.md).
  //
  // Pair the paint with a fire-and-forget thumbnail refresh for bookshop
  // cards whose stored photoUrl is a signed URL nearing its 1h TTL. The
  // refresh fetch runs in the background; if it returns a new path the
  // card is patched and re-rendered, otherwise the initial paint stands.
  // clearPurchasedResumeCards() runs alongside it to drop cards whose
  // kid's order has landed.
  function initialPaint() {
    renderResumeCard();
    refreshBookshopThumbnails();
    clearPurchasedResumeCards();
    rehydrateStaleGenerating();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialPaint);
  } else {
    initialPaint();
  }

  // Back/forward navigation. When the browser restores this page from its
  // back-forward cache, the script does NOT re-execute and DOMContentLoaded
  // does NOT fire — the whole previous DOM is restored instead. If that DOM
  // was captured BEFORE the customer's resume record existed (they landed
  // here, opened the funnel, generated, then hit back), the restored page
  // has no card, and nothing above repaints it. To the customer their
  // progress is simply gone: no card, no way back into the bookshop, so
  // they start over and pay for a second generation. It reappears only on
  // some later real page load, which reads as the card "magically coming
  // back" (Lindsey QA 2026-08-22).
  //
  // pageshow fires on both fresh loads and restores; `persisted` marks the
  // restore case, which is the one DOMContentLoaded misses.
  if (typeof window !== 'undefined') {
    window.addEventListener('pageshow', function (e) {
      if (e && e.persisted) initialPaint();
    });
  }

  // Expose internals for unit tests (vitest + happy-dom). No-op in prod
  // browsers — `__SCRIBBLY_TEST_API__` isn't read by any production code.
  if (typeof window !== 'undefined') {
    window.__SCRIBBLY_TEST_API__ = {
      saveResume: saveResume,
      loadSaved: loadSaved,
      loadAll: loadAll,
      clearSaved: clearSaved,
      dismissKid: dismissKid,
      patchSaved: patchSaved,
      recordKey: recordKey,
      sameKid: sameKid,
      pipelineProgress: pipelineProgress,
      pipelineProgressTimed: pipelineProgressTimed,
      formatElapsed: formatElapsed,
      namePossessive: namePossessive,
      photoUrlOK: photoUrlOK,
      photoPathChanged: photoPathChanged,
      renderResumeCard: renderResumeCard,
      refreshBookshopThumbnails: refreshBookshopThumbnails,
      clearPurchasedResumeCards: clearPurchasedResumeCards,
      rehydrateStaleGenerating: rehydrateStaleGenerating,
      resumeCardVariant: resumeCardVariant,
      shouldRehydrateOnReturn: shouldRehydrateOnReturn,
      showCapToast: showCapToast,
      hideCapToast: hideCapToast,
      rewriteLegacyFunnelLinks: rewriteLegacyFunnelLinks,
      findFunnelTrigger: findFunnelTrigger,
      matchesFunnelPattern: matchesFunnelPattern,
      // Constants used by the funnel TS source — exported so a test can
      // assert that the parent script's mirror matches the source of truth.
      _constants: {
        PIPELINE_BANDS: PIPELINE_BANDS,
        STORY_SLIDE_MS: STORY_SLIDE_MS,
        STORY_SLIDE_COUNT: STORY_SLIDE_COUNT,
        PROGRESS_HOLD_CEILING: PROGRESS_HOLD_CEILING,
        EXPECTED_TOTAL_MS: EXPECTED_TOTAL_MS,
        DONE_SNAP_MS: DONE_SNAP_MS,
        POLL_INTERVAL_MS: POLL_INTERVAL_MS,
        POLL_HARD_CAP_MS: POLL_HARD_CAP_MS,
        STANDBY_AFTER_MS: STANDBY_AFTER_MS,
        RESUME_TTL_MS: RESUME_TTL_MS,
        MAX_KIDS: MAX_KIDS,
        RESUME_SCHEMA_VERSION: RESUME_SCHEMA_VERSION,
        FUNNEL_URL: FUNNEL_URL,
        FUNNEL_LINK_PATTERNS: FUNNEL_LINK_PATTERNS
      }
    };
  }
})();
